import { Checkbox } from '@/components/ui/checkbox';
import SavedBookingDetails from './SavedBookingDetails';
import {appointmentDateTime} from './AppointmentHistory';
import { PortalApiError } from '@/lib/portal-api';
import { Button } from '@/components/ui/button';
/**
 * TRAIN-B-CLINIC-DAY-HANDOFF-V1 -- the clinic DAY BOARD.
 *
 * ONE day surface for the clinic-in-context: today's appointments (the FULL
 * stored-status day, terminal rows included) with a status chip from the
 * ACTUAL taxonomy, a derived ops-status column (visit / outcome / payment
 * states), and per-row actions wired to the EXISTING manage paths only.
 * Reads GET /api/appointments/today?view=board -- the SAME computation the
 * walk-in Today plan reads, widened server-side (ONE-DAY-SURFACE LAW). The
 * caller fetches; this module renders + fires the existing mutation client
 * fns and asks the caller to refetch (onChanged).
 *
 * ROLE LENSES (frontend mirror of the backend DAY_BOARD_ROLES fence +
 * the role x clinical matrix, docs/AGENT-RUNS/TRAIN-B-CLINIC-DAY-HANDOFF-V1
 * section 4). UX only -- the backend route auth is the security boundary:
 *   - receptionist: full action strip (confirm / arrived / check-in /
 *     fulfilled / cancel / no-show -- the EXISTING APPOINTMENT_ACTION_ROLES
 *     paths) + the Record-payment door where the row supports it.
 *   - coc / owner / gm_agm / regional_head: VIEW-ONLY (SEE-NOT-DO; recorded
 *     ambiguity->narrower ruling D-5) -- rows + ops-status, no actions.
 *   - doctor: view + the case-sheet door (added by the doctor-home lens);
 *     the manage strip never renders (doctor is not an action role).
 *   - call_center_agent / patient: never see the board (server 401s
 *     ?view=board; callers must not request it -- canSeeDayBoard mirror).
 *
 * Reschedule stays on the Lead-360 appointment workbench (the drawer's
 * full-view door): the ONE shared slot grid lives there per the
 * BUTTON-STATE + EXPLICIT-INTENT law (V5-P14) -- the day board offers the
 * one-press day hops, never a second picker wiring.
 *
 * PHI: rows carry NO phone (the read never selects it); the audited
 * Lead-360 reveal stays the only contact door. Clinical content: the server
 * ships the category token to the doctor only; this component renders what
 * it is given and NEVER does the hiding itself.
 *
 * Testability (repo idiom -- react-dom/server, no jsdom): the pure helpers
 * + ClinicDayBoardView render from props; the default container wires
 * state around the injected client fns.
 *
 * FRONT-OFFICE-DENSITY-PASS-V1 (R-I DENSITY LAW; founder ruling 2026-07-29) --
 * PRESENTATION AND COMPOSITION ONLY. Zero wire change, zero new workflow, zero
 * new permission, zero new fact:
 *   D1  Status + Ops status collapse into ONE State cell, max TWO chips, ranked
 *       by dayStateCell (payment > conversion > visit > reason). The stored
 *       status chip stays BYTE-IDENTICAL (R-C: machine states frozen; the label
 *       + tone collapse rides rider R-40). Overflow renders "+N" and stays
 *       reachable in the row's EXISTING drawer (EVIDENCE-ROWS law).
 *   D2  ONE next action per row (dayBoardNextAction, modelled on the P7
 *       reference machine): one primary, at most one visible secondary, the rest
 *       behind a disclosure -- ACTION LENS ONLY. dayBoardActions verdicts are
 *       unchanged; this ranks them.
 *   D3  Waiting time by REUSE of waitingMinutes (patient-360-view.ts), gated
 *       exactly as Patient 360 gates it, clock injected.
 *   D5  The table rides the shared .ac-table-scroll container (03 P10) and every
 *       cell carries its own data-label so the <=720px stack stays LABELED.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CalendarDays } from 'lucide-react';

import {
  assignAppointmentDoctor,
  confirmAppointment,
  cancelAppointment,
  markAppointmentArrived,
  markAppointmentCheckedIn,
  markAppointmentFulfilled,
  markAppointmentNoShow,
} from '@/dashboard/api';
import type { ClinicRosterDoctor, TodayAppointment } from '@/dashboard/types';
import {
  appointmentErrorMessage,
  ARRIVED_BUTTON_COPY,
  CHECKED_IN_BUTTON_COPY,
  ARRIVED_SUCCESS_COPY,
  CANCEL_BUTTON_COPY,
  CANCEL_SUCCESS_COPY,
  CHECKED_IN_SUCCESS_COPY,
  CONFIRM_SUCCESS_COPY,
  FULFILLED_BUTTON_COPY,
  FULFILLED_SUCCESS_COPY,
  NO_SHOW_BUTTON_COPY,
  NO_SHOW_CONFIRM_COPY,
  NO_SHOW_SUCCESS_COPY,
  allowedWorkbenchActions,
  type WorkbenchActionKind,
} from '@/dashboard/owner/AppointmentWorkbench';
import {
  AcBadge,
  FieldGrid,
  AcButton,
  ConfirmDialog,
  ModalShell,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusChip,
  appointmentStatusDisplayLabel,
  appointmentStatusDisplayTone,
  dayScopeEmptyCopy,
  dayScopeReadOnlyCopy,
  doctorConsultOpen,
  type AcBadgeTone,
  type AppointmentStatusLabelOverrides,
  type DayScopeKind,
  type StatusChipDomain,
} from '@/dashboard/ui';
import { formatDurationHm, formatIstDateTime } from '@/lib/datetime-ist';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import DoctorQueueContextDrawer from '@/dashboard/doctor/DoctorQueueContextDrawer';
import CancelReasonDialog from '@/dashboard/appointments/CancelReasonDialog';
import AssignDoctorDialog from '@/dashboard/appointments/AssignDoctorDialog';
import IntakeDrawer from '@/dashboard/appointments/IntakeDrawer';
import CompleteRegistrationModal from '@/dashboard/appointments/CompleteRegistrationModal';
import {
  intakeRowPlan,
} from '@/dashboard/appointments/intake-next-action';
import CheckinIdentityStep, {
  type CheckinIdentityAction,
} from '@/dashboard/appointments/CheckinIdentityStep';
import {
  cancelReasonLabel,
  DEFAULT_CANCEL_REASON_CODES,
} from '@/dashboard/appointments/cancel-reasons';
import {
  derivePaymentHandoff,
  visitClosedForHandoff,
} from '@/dashboard/appointments/payment-handoff';
import {
  checkedInSuccessFeedback,
  checkoutHandoff,
} from '@/dashboard/appointments/workflow-handoff';
import {
  acceptedOutcomeLabel,
  resolveOutcomeSignals,
  VISIT_DESK_CLOSED_LABEL,
  type ConversionKind,
} from '@/dashboard/record/current-truth';
import {
  deriveDoctorQueueState,
  doctorQueueLabel,
  waitingMinutes,
  type DoctorQueueState,
} from '@/dashboard/patients/patient-360-view';
import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import { formatIstTime } from '@/lib/datetime-ist';

// --- Role lenses (frontend mirror; backend is the boundary) ---------------

/** Mirror of the backend DAY_BOARD_ROLES (appointments-read.ts). */
export const DAY_BOARD_VIEW_ROLES: readonly string[] = [
  'receptionist',
  'coc',
  'doctor',
  'owner',
  'gm_agm',
  'regional_head',
];

/** Whether this role may request/see the board view at all. */
export function canSeeDayBoard(role: string | null | undefined): boolean {
  return typeof role === 'string' && DAY_BOARD_VIEW_ROLES.includes(role);
}

/** The roles whose DAY-BOARD lens renders the manage action strip. A UI
 *  reduction of the backend APPOINTMENT_ACTION_ROLES per the matrix ruling
 *  (coc view-only on THIS surface, D-5; owner/managers monitor-not-operate;
 *  doctor is not an action role). Backend route auth stays the boundary. */
export const DAY_BOARD_ACTION_ROLES: readonly string[] = ['receptionist'];

export function canActOnDayBoard(role: string | null | undefined): boolean {
  return typeof role === 'string' && DAY_BOARD_ACTION_ROLES.includes(role);
}

/**
 * CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1 (DOCTOR-CONTEXT LAW): which drawer
 * a board row's name-click opens, PER ROLE -- links are role-shaped or
 * absent:
 *   - doctor -> the doctor-safe context drawer (keys on the APPOINTMENT id,
 *     so it works for lead-less walk-in rows too). NEVER the Lead workspace
 *     drawer (its backing read rejects the doctor -- the founder's broken
 *     403 door, 2026-07-09/10).
 *   - receptionist / coc / owner -> the ONE LeadQuickView (they hold the
 *     leads-workspace read; DRAWER LAW unchanged) on lead-linked rows.
 *   - gm_agm / regional_head -> NO link (they hold neither read today; the
 *     old wiring sent them into the same broken drawer -- their read-down
 *     stays at the aggregate layer, doctrine 06 rule 3).
 */
const LEAD_DRAWER_ROLES: readonly string[] = ['receptionist', 'coc', 'owner'];

export type RowOpenKind = 'doctor_context' | 'lead_quickview' | 'none';

export function rowOpenKind(
  role: string | null | undefined,
  row: Pick<TodayAppointment, 'leadId'>,
): RowOpenKind {
  if (role === 'doctor') return 'doctor_context';
  if (
    typeof role === 'string' &&
    LEAD_DRAWER_ROLES.includes(role) &&
    row.leadId != null
  ) {
    return 'lead_quickview';
  }
  return 'none';
}

/**
 * The patient label for a board row -- NEVER "Unknown patient"
 * (DOCTOR-ONE-CLOSE-AND-TRUST-V1).
 *
 * V-2 REQ-PATIENT-NAME-SINGLE-SOURCE (founder-review corrective on PR #271):
 * this is a PATIENT-CONTEXT operational surface (the Front Desk Today / day
 * board, and the IntakeDrawer/appointment operational rows that reuse it), so
 * the LINKED golden-record display name (linkedPatientName = patients.full_name,
 * populated whenever appointments.patient_id resolves) is PRIMARY -- it wins
 * over the appointment booking name. This ends the founder-walk divergence
 * where the same linked patient showed the booking name ("adithya test") here
 * while the Patient Master ("adi tes") showed on the payment surfaces. The
 * booking name (appointments.patient_name) is the honest fallback ONLY for an
 * UNLINKED row (no golden record to diverge from); a lead row with no name
 * anywhere stays the honest pending enquiry; an unlinked nameless row is an
 * unnamed appointment. Zero phone (the board wire never carries one). No writer
 * change: appointment.patient_name is untouched -- render selection only.
 */
export function dayBoardPatientLabel(
  row: Pick<TodayAppointment, 'patientName' | 'leadId' | 'linkedPatientName'>,
): string {
  const linkedName = row.linkedPatientName?.trim();
  if (linkedName) return linkedName;
  const name = row.patientName?.trim();
  if (name) return name;
  if (row.leadId != null) return 'Enquiry - name pending';
  return 'Unnamed appointment';
}

// --- Assigned doctor + queue readiness (SEP15 vertical) ---------------------

/** The explicit no-assignment truth (charter outcome 1: "assigned doctor or
 *  explicit Unassigned"). Still the honest state wherever the S2
 *  auto-assignment rule is off/unmapped and no manual assignment happened
 *  (DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1 shipped both writers). */
export const DOCTOR_UNASSIGNED_LABEL = 'Unassigned';

/** DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: the statuses on which the
 *  assign/reassign act is offered -- the frontend mirror of the backend
 *  assign_doctor allowed-from set (appointment-status.ts; UI gating only,
 *  the route guard is the boundary). A terminal / fulfilled row is never
 *  reassigned. */
export const DAY_BOARD_ASSIGNABLE_STATUSES: readonly string[] = [
  'booked',
  'confirmed',
  'arrived',
  'checked_in',
];

export function canAssignDoctorOnRow(status: string): boolean {
  return DAY_BOARD_ASSIGNABLE_STATUSES.includes(status);
}

/** The assign affordance's label: honest verb per current truth. */
export const ASSIGN_DOCTOR_BUTTON_COPY = 'Assign';
export const REASSIGN_DOCTOR_BUTTON_COPY = 'Change';
export const ASSIGN_DOCTOR_SUCCESS_COPY = 'Doctor assigned.';

/** The row's doctor cell text: the roster name (server-resolved via the SAME
 *  doctors join Patient 360 uses) or the explicit Unassigned truth. */
export function assignedDoctorLabel(
  row: Pick<TodayAppointment, 'assignedDoctorName'>,
): string {
  const name = row.assignedDoctorName?.trim();
  return name ? name : DOCTOR_UNASSIGNED_LABEL;
}

/**
 * The row's doctor/queue READINESS line -- consumed from THE ONE evaluator
 * (deriveDoctorQueueState, patient-360-view.ts: "the ONLY place any surface
 * derives a doctor/queue readiness label"), never a second derivation
 * (REQ-PATIENT-DOCTOR-QUEUE-CONSISTENCY). Renders ONLY the two queue-speaking
 * states ('ready_for_doctor' / 'in_queue_unassigned'): every other state is
 * already spoken by an existing chip family (status chip, In-consultation
 * ops chip, terminal chips) -- one fact renders once.
 *
 * Inputs map 1:1 onto the evaluator's contract from EXISTING board wire
 * facts: canonicalStatus (server-projected via THE resolver), arrivedAt,
 * assignment truth (roster name resolved), the consult_started event, and
 * the visit-record state (finalized -> completed, draft -> in_progress).
 */
export function dayBoardQueueReadiness(
  row: Pick<
    TodayAppointment,
    | 'canonicalStatus'
    | 'arrivedAt'
    | 'assignedDoctorName'
    | 'consultStarted'
    | 'visitStatus'
    | 'hasPatientRecord'
    | 'registrationCaptured'
  >,
): { state: DoctorQueueState; label: string } | null {
  const state = deriveDoctorQueueState({
    canonicalStatus: row.canonicalStatus ?? null,
    arrivedAt: row.arrivedAt ?? null,
    hasDoctorAssigned:
      typeof row.assignedDoctorName === 'string' &&
      row.assignedDoctorName.trim() !== '',
    consultStarted: row.consultStarted === true,
    clinicalState:
      row.visitStatus === 'finalized'
        ? 'completed'
        : row.visitStatus === 'draft'
          ? 'in_progress'
          : 'not_started',
    // F8 (AUG-12-WALK-2-REGISTRATION-TRUTH-V1): the SAME identity-or-
    // registration gate the doctor's Start door reads (D4) -- an arrived +
    // registered row derives "Ready for doctor" / in-queue; never stored,
    // never a new token.
    startGateOpen:
      row.hasPatientRecord === true || row.registrationCaptured === true,
  });
  if (state !== 'ready_for_doctor' && state !== 'in_queue_unassigned') {
    return null;
  }
  return {
    state,
    label: doctorQueueLabel(state, row.assignedDoctorName?.trim() || null),
  };
}

/**
 * AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F7, founder walk W-11), relabeled by
 * CONVERSION-CONSEQUENCE-MINT-V1 (founder ruling 2026-08-08, W-20): the
 * front-desk row's identity fact in PLAIN WORDS -- the machine-state chip
 * "Patient link pending" stays retired. Registration state and link state
 * are SEPARATE facts, derived from the SAME wire truths the doctor door
 * reads: an unlinked lead row that IS registered reads "Ready for consult"
 * (the desk's work is DONE; the mint is the consult's own consequence --
 * never "registers at consult", which misreads as registration still
 * owed); one that is NOT registered names the actual work ("Registration
 * pending" -- the doctor-hint vocabulary, one word set across roles).
 * Linked rows say nothing (the UMR speaks), and a row whose consult is
 * already running or recorded says nothing here either -- the ops chip
 * owns that state, and a readiness claim on it would be false.
 *
 * CONSULT-INTAKE-RENDER-HEAL-V1 (scope 3b) RECONCILED ONTO #508: the
 * stale-badge fix ("Ready for consult") is #508 main truth (its derivation
 * WINS) -- this package adopts it unchanged. This package's day-board work is
 * the doctor-lens one-current-state display (the State cell below) + the
 * front-desk Review-intake removal (intake-next-action.ts); it does NOT
 * re-change the shared Doctor startability rule. Reception separately requires
 * actual check-in before saying ready: registration can save while check-in is
 * still pending, so that row reports the saved registration fact instead.
 */
export function dayBoardIdentityChip(
  row: Pick<
    TodayAppointment,
    'leadId' | 'patientId' | 'hasPatientRecord' | 'registrationCaptured' | 'consultStarted' | 'visitStatus'
  > & Partial<Pick<TodayAppointment, 'status'>>,
  role?: string | null,
): string | null {
  if (row.leadId == null || row.hasPatientRecord === true || row.registrationCaptured === undefined) return null;
  if (
    row.visitStatus === 'draft' ||
    row.visitStatus === 'finalized' ||
    row.consultStarted === true
  ) {
    return null;
  }
  return row.registrationCaptured === true
    ? role === 'receptionist' && row.status !== 'checked_in'
      ? 'Registration saved'
      : 'Ready for consult'
    : 'Registration pending';
}

// --- Per-state header counts (SEP15 vertical) -------------------------------

/** The display ladder for the counts strip -- the stored vocabulary in
 *  forward-ladder order (dayStatusChip is the ONE label home; unknown stored
 *  tokens group under their humanized label at the end). */
const DAY_COUNT_LADDER: readonly string[] = [
  'waitlist',
  'booked',
  'confirmed',
  'rescheduled',
  'arrived',
  'checked_in',
  'fulfilled',
  'completed',
  'cancelled',
  'no_show',
];

export interface DayStateCount {
  readonly status: string;
  readonly label: string;
  readonly count: number;
}

/** Per-state counts derived from the SAME fetched rows the list renders --
 *  counts == list BY CONSTRUCTION (one derivation, zero second reads).
 *  Only states present today are returned (an empty day renders the honest
 *  empty state, never a zero wall). Unknown stored tokens are counted, not
 *  hidden (the board never silently drops a real row). */
export function dayStateCounts(
  rows: readonly TodayAppointment[],
  orgLabels?: AppointmentStatusLabelOverrides | null,
): DayStateCount[] {
  const byStatus = new Map<string, number>();
  for (const r of rows) {
    byStatus.set(r.status, (byStatus.get(r.status) ?? 0) + 1);
  }
  const out: DayStateCount[] = [];
  for (const status of DAY_COUNT_LADDER) {
    const count = byStatus.get(status);
    if (count !== undefined && count > 0) {
      out.push({ status, label: dayStatusChip(status, orgLabels).label, count });
      byStatus.delete(status);
    }
  }
  // Unknown-but-real stored tokens (defensive; the chip map humanizes them).
  for (const [status, count] of byStatus) {
    out.push({ status, label: dayStatusChip(status, orgLabels).label, count });
  }
  return out;
}

// --- FD-42 (RECEPTION-FIX-AND-POLISH-BATCH-V1): the desk ACTION counts ------

/**
 * THE summary-strip derivation (FD-42; RECEPTION LAW walk finding): the
 * role questions -- who's WAITING / who's LATE / who NEEDS PAYMENT /
 * who NEEDS REGISTRATION -- derived from the SAME fetched rows the board
 * renders (counts == list by construction; zero second reads). The
 * needs-payment half consumes THE ONE collect derivation
 * (collectQueueSummary, payment-handoff.ts) at the CALLER (board-mode
 * only -- the plain wire lacks the derivation fields), so this function
 * carries the others and the caller composes.
 * EXISTING-SCREEN-COMPLETIONS-V1 adds the CANCELLED day tally (tenant
 * tracker, FRONT DESK home dashboard row) to the same one-pass derivation.
 */
export interface DeskActionCounts {
  /** In the clinic, before the doctor: arrived + checked_in. */
  readonly waiting: number;
  /** Past their booked time, not yet arrived (booked/confirmed/
   *  rescheduled with appointmentTime < now). */
  readonly late: number;
  /** Arrived/checked-in WITHOUT a linked golden record -- the
   *  registration checkpoint is still owed. */
  readonly needsRegistration: number;
  /** EXISTING-SCREEN-COMPLETIONS-V1 (tenant tracker, FRONT DESK home
   *  "Cancelled"): the day's recorded cancelled tally, from the SAME rows
   *  (stored status only -- the same predicate the day-state strip and the
   *  COC day-flow Cancelled tile count, so the numbers agree across
   *  surfaces by construction). */
  readonly cancelled: number;
}

export function deskActionCounts(
  rows: readonly TodayAppointment[],
  nowMs: number,
): DeskActionCounts {
  let waiting = 0;
  let late = 0;
  let needsRegistration = 0;
  let cancelled = 0;
  for (const r of rows) {
    if (r.status === 'arrived' || r.status === 'checked_in') {
      if (dayBoardIsWaiting(r)) waiting += 1;
      if (r.hasPatientRecord !== true && r.registrationCaptured !== true) needsRegistration += 1;
      continue;
    }
    if (r.status === 'cancelled') {
      cancelled += 1;
      continue;
    }
    if (
      (r.status === 'booked' ||
        r.status === 'confirmed' ||
        r.status === 'rescheduled') &&
      Date.parse(r.appointmentTime) < nowMs
    ) {
      late += 1;
    }
  }
  return { waiting, late, needsRegistration, cancelled };
}

/** Sep25 shared Home summary, over the selected clinic-day rows. The
 * clinical predicate is the same one used by each Reception state chip;
 * closing the desk visit does not assert that clinical paperwork is done. */
export function receptionDaySummary(rows: readonly TodayAppointment[] | null) {
  if (rows === null) return null;
  return {
    waiting: rows.filter(dayBoardIsWaiting).length,
    inConsult: rows.filter(row => (row.status === 'arrived' || row.status === 'checked_in') && doctorConsultOpen(row)).length,
    completed: rows.filter(row => isCheckedOutRow(row) || row.visitStatus === 'finalized').length,
    expected: rows.filter(row => ['booked', 'confirmed', 'rescheduled', 'waitlist'].includes(row.status)).length,
    cancelled: rows.filter(row => row.status === 'cancelled').length,
    newVisits: rows.filter(row => row.linkedPatientPriorVisit === 'new').length,
    unknownHistory: rows.some(row => row.linkedPatientPriorVisit !== 'new' && row.linkedPatientPriorVisit !== 'returning'),
  };
}

// --- FRONT-DESK-DAY-BOARD-V1: the CHECKED-OUT view over the SAME rows -------

/**
 * Partha TAB row "CHECK OUT PATIENTS": the desk-closed stored pair --
 * `fulfilled` (the desk closed the visit; the R-1 fast path included) and
 * `completed` (the terminal close). EXACTLY the pair the board's own
 * desk-closed honesty line (VISIT_DESK_CLOSED_LABEL) and payment branches
 * already speak -- one vocabulary, never a third status family. The view is
 * a CLIENT-SIDE cut over the SAME fetched day rows (zero second reads); the
 * host's head counts keep describing the WHOLE day, so the tally and the
 * narrowed table can never claim different days.
 */
export const DAY_CHECKED_OUT_STATUSES: readonly string[] = [
  'fulfilled',
  'completed',
];

export function isCheckedOutRow(
  row: Pick<TodayAppointment, 'status'>,
): boolean {
  return DAY_CHECKED_OUT_STATUSES.includes(row.status);
}

/** The two named day views (ViewChips vocabulary; C-11 role defaults). */
export type DayBoardViewId = 'everyone' | 'checked_out';

export const DAY_BOARD_VIEWS: readonly {
  readonly id: DayBoardViewId;
  readonly label: string;
  readonly hint: string;
}[] = [
  {
    id: 'everyone',
    label: 'Everyone',
    hint: 'Every appointment on this day',
  },
  {
    id: 'checked_out',
    label: 'Checked out',
    hint: 'Visits the desk has closed - collect or review',
  },
];

/** Normalize a URL-carried view token; anything unknown is the full day
 *  (fail-open to the honest whole, never a silently narrowed board). */
export function dayBoardViewId(raw: string | null | undefined): DayBoardViewId {
  return raw === 'checked_out' ? 'checked_out' : 'everyone';
}

/** The rows one view renders -- a pure cut, served order kept. */
export function dayBoardViewRows(
  rows: readonly TodayAppointment[],
  viewId: DayBoardViewId,
): TodayAppointment[] {
  return viewId === 'checked_out' ? rows.filter(isCheckedOutRow) : [...rows];
}

/** The honest filtered-empty line: the DAY may have rows while the VIEW has
 *  none, so the board's no-appointments empty copy must not speak here. */
export const DAY_VIEW_CHECKED_OUT_EMPTY_COPY =
  'No checked-out patients on this day yet.';

// --- Patient-record continuity door (SEP15 vertical) ------------------------

/** Roles offered the Patient 360 door from a LINKED board row. The backing
 *  read is the patient-master record surface (requirePatientMasterScope --
 *  receptionist/coc/gm_agm/owner + merge tier); the DOOR renders only for
 *  the roles that already hold a row door on this board (LES-016: links are
 *  role-shaped or absent; managers' read-down stays at the aggregate layer,
 *  doctrine 06 rule 3; the doctor's patient context is the doctor drawer +
 *  lens, never the Patient Master surface). */
export const PATIENT_RECORD_DOOR_ROLES: readonly string[] = [
  'receptionist',
  'coc',
  'owner',
];

export function canOpenPatientRecordFromBoard(
  role: string | null | undefined,
): boolean {
  return typeof role === 'string' && PATIENT_RECORD_DOOR_ROLES.includes(role);
}

// --- Q-08=A: the Booking channel render (RECEPTION-OS-BUILD-V1) -------------

/** The RULED field label (founder letter Q-08=A: keep the incumbent-named
 *  column for Sept; RENDER it as "Booking channel"; the rename is a named
 *  follow-up). */
export const BOOKING_CHANNEL_FIELD_LABEL = 'Booking channel';

/** Display words for the CHECK vocabulary (walkin/telephonic/online). An
 *  unknown token humanizes -- never hidden, never invented. */
export function bookingChannelLabel(token: string): string {
  if (token === 'walkin') return 'Walk-in';
  if (token === 'telephonic') return 'Phone';
  if (token === 'online') return 'Online';
  if (token === 'paid_followup') return 'Paid follow-up';
  const spaced = token.replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : token;
}

// --- Status chips (the ACTUAL stored taxonomy; STATUS-TAXONOMY LAW) --------

interface DayStatusChip {
  readonly label: string;
  readonly tone: AcBadgeTone;
}

/** Chip per STORED status (schema_master appointments_status CHECK, 10
 *  values). RECEPTION-OS-BUILD-V1 (F-09): the board's screen-local label map
 *  died -- label AND tone now resolve through THE ONE StatusChip canon
 *  (ui/status-canon.ts), org-config labels first (`appointment.status_labels`
 *  via /me -- F-12's config half). Unknown values still humanize (the canon's
 *  de-cased fallback) rather than hide -- the board never silently drops a
 *  real row. The STORED vocabulary is byte-unchanged (VOCABULARY LAW). */
export function dayStatusChip(
  status: string,
  orgLabels?: AppointmentStatusLabelOverrides | null,
): DayStatusChip {
  return {
    label: appointmentStatusDisplayLabel(status, orgLabels),
    tone: appointmentStatusDisplayTone(status),
  };
}

// --- Ops-status derivation (display of the server-derived tokens) ---------

/**
 * FRONT-OFFICE-DENSITY-PASS-V1 (D1): the ops-chip FAMILY tag. Explicit on
 * every chip so the State cell's precedence is a data lookup, never a label
 * string match (a label rename must never silently re-rank a row). The four
 * families are the four facts the ops column ever spoke: money, commercial
 * outcome, visit state, cancel reason.
 */
export type DayOpsFamily = 'payment' | 'conversion' | 'visit' | 'reason';

export interface DayOpsChip {
  readonly label: string;
  readonly tone: AcBadgeTone;
  /** D1: which fact this chip speaks (drives the State-cell precedence). */
  readonly family: DayOpsFamily;
  /** THE StatusChip canon coordinates (V7-P4 continued adoption): set for
   *  the conversion / payment / follow-up chip families, whose canon tones
   *  PROVABLY equal this board's proven tones (equality is test-pinned in
   *  clinic-day-board.test.tsx) -- the chip then renders through the
   *  StatusChip canon with this screen's label as the caller-resolved
   *  label (the labels stay the pinned source; ROLE-LENS-DESIGN-SYSTEM-V1
   *  adoption contract). Absent for the visit-state / stored-status /
   *  reason chips, whose local tones DIVERGE from the canon -- those stay
   *  on AcBadge untouched (divergence carried by rider R-40, never silently
   *  re-toned). */
  readonly canon?: {
    readonly domain: StatusChipDomain;
    readonly status: string;
  };
}

/** Statuses where a visit record is EXPECTED (the patient reached the
 *  chair) -- the honest "Visit not started" seam renders only from here. */
const VISIT_EXPECTED_STATUSES: readonly string[] = [
  'checked_in',
  'fulfilled',
  'completed',
];

/** RECORD-360-WORKSPACE-V1: ONE current conversion chip per row, from the
 *  SAME per-record outcome resolution the record workspace uses
 *  (current-truth.ts) -- Converted + Not converted can never co-render
 *  (E-360-2), and a raw token pile never masquerades as current truth. */
const DAY_CONVERSION_CHIP: Readonly<
  Record<Exclude<ConversionKind, 'none'>, DayOpsChip>
> = {
  awaiting_decision: {
    label: 'Advised - decision pending',
    tone: 'info',
    family: 'conversion',
    canon: { domain: 'conversion', status: 'awaiting_decision' },
  },
  // V-1: the 'converted' kind's LABEL is payment-gated at push time
  // (acceptedOutcomeLabel) -- "Treatment accepted" pre-payment, the reserved
  // "Converted" only behind active first-payment evidence. This map entry is
  // the pre-payment default; the push below overrides it with the paid label.
  converted: {
    label: 'Treatment accepted',
    tone: 'positive',
    family: 'conversion',
    canon: { domain: 'conversion', status: 'converted' },
  },
  partially_accepted: {
    label: 'Partially accepted',
    tone: 'info',
    family: 'conversion',
    canon: { domain: 'conversion', status: 'partially_accepted' },
  },
  not_converted: {
    label: 'Not converted',
    tone: 'danger',
    family: 'conversion',
    canon: { domain: 'conversion', status: 'not_converted' },
  },
  contradiction: {
    label: 'Conflicting records - needs review',
    tone: 'danger',
    family: 'conversion',
    canon: { domain: 'conversion', status: 'contradiction' },
  },
};

/**
 * THE in-consultation predicate -- now a CONSUMER of the ONE doctor
 * visit-state owner (DOCTOR-HOME-STATE-MODEL-V1 F1: the board consumes the
 * owner, it never re-derives; this was one of four duplicated copies of the
 * rule). Same law as before -- draft-or-started, never on a finalized or
 * ended visit -- with the owner's two honesty tightenings: a started event
 * on an ARRIVED row counts (the D4 arrived-is-startable law), and a stale
 * started event on a departed sheet-less row does not. Consumed by the
 * board's ops chip AND the manager cockpit's In-consult tile, so the tile
 * mirrors the chip by construction.
 */
export function isInConsultation(row: TodayAppointment): boolean {
  return doctorConsultOpen(row);
}

/** Reception assignment locks permanently once authoritative visit facts show a start.
 * This differs from an open consultation: finalization must not unlock the doctor. */
export function receptionDoctorAssignmentLocked(row: TodayAppointment): boolean {
  return row.consultStarted === true || row.visitStatus === 'draft' || row.visitStatus === 'finalized';
}

/** Current visit stage shared by compact Home and its quick view; stored status stays unchanged. */
export function currentVisitStatusChip(row: TodayAppointment, labels?: AppointmentStatusLabelOverrides | null): DayStatusChip {
  if (row.visitStatus === 'finalized') return { label: 'Visit complete', tone: 'positive' };
  if (isInConsultation(row)) {
    return dayOpsChips(row).find(chip => chip.family === 'visit') ?? dayStatusChip(row.status, labels);
  }
  return dayStatusChip(row.status, labels);
}

/**
 * MONEY-COMPLETION-V1 (rider R-52): THE one board-row outcome-signals call
 * (the F-CT-3 POLAR law inputs -- plan COUNTS feed the verdict ONLY on a
 * FINALIZED visit; events always count). Extracted so the chips AND the
 * receptionist payment door consume IDENTICAL signals -- the R-52
 * divergence class was two consumers assuming each other's inputs.
 */
export function dayRowOutcomeSignals(row: TodayAppointment): ConversionKind {
  const finalized = row.visitStatus === 'finalized';
  return resolveOutcomeSignals({
    outcomeEvents: row.outcomeEvents ?? [],
    acceptedPlanCount: finalized ? row.acceptedPlanCount : undefined,
    advisedPlanCount: finalized ? row.advisedPlanCount : undefined,
  });
}

/**
 * The ops-status chips for one board row: visit state (SEAM-1 -- rendered
 * honestly, never auto-created; the fulfilled-with-no-record pair reads the
 * honest desk-closed line, E-360-5), ONE derived conversion chip, and
 * payment evidence. Pure derivation of server-shipped fields; nothing is
 * invented client-side.
 */
export function dayOpsChips(row: TodayAppointment): DayOpsChip[] {
  const chips: DayOpsChip[] = [];
  const visit = row.visitStatus;
  if (visit === 'finalized') {
    chips.push({ label: 'Visit finalized', tone: 'positive', family: 'visit' });
  } else if (isInConsultation(row)) {
    // CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1: the plain-words ladder label
    // ("In consult = with the doctor"); was "Visit in progress" -- a
    // DELIBERATE rename, run-record section 3.
    // FRONTDESK-CHECKIN-AND-IDENTITY-V1 (rider R-07): the chip now ALSO
    // flips on the consult_started EVENT (the doctor's Start/Resume click,
    // sheet not yet begun) while the row is still in the chair
    // (checked_in) -- the draft-autosave signal stays the fallback, so the
    // chip lights on the CLICK, never the first keystroke, once the founder
    // applies 20260711_1. Terminal / desk-closed rows keep their own honest
    // branches below.
    chips.push({ label: 'In consult', tone: 'pending', family: 'visit' });
  } else if (visit === 'none' && VISIT_EXPECTED_STATUSES.includes(row.status)) {
    // E-360-5: a FULFILLED/COMPLETED row with no doctor record is the legal
    // desk-closed path (incl. the R-1 front-desk fast path) -- say so in one
    // honest line instead of the confusing "Fulfilled + Visit not started".
    chips.push(
      row.status === 'fulfilled' || row.status === 'completed'
        ? { label: VISIT_DESK_CLOSED_LABEL, tone: 'neutral', family: 'visit' }
        : { label: 'Visit not started', tone: 'neutral', family: 'visit' },
    );
  }
  // F-CT-3 POLAR law inputs via THE one board-row call (rider R-52).
  const conversion = dayRowOutcomeSignals(row);
  // 'awaiting_decision' is deliberately NOT chipped here: the handoff chip
  // below already speaks that state as the actionable "Follow-up needed"
  // (one fact, one chip -- adversarial-review finding 10).
  if (conversion !== 'none' && conversion !== 'awaiting_decision') {
    // V-1 REQ-COMMERCIAL-VOCABULARY-UNIFICATION: the accepted outcome reads
    // "Treatment accepted" until an ACTIVE non-voided first payment lands,
    // then the reserved "Converted" (row.paymentRecorded is void-excluded, so
    // a later void falls the label back). This ends the founder-walk collision
    // where "Converted" showed next to "Payment pending" pre-payment.
    chips.push(
      conversion === 'converted'
        ? {
            label: acceptedOutcomeLabel(row.paymentRecorded === true),
            tone: 'positive',
            family: 'conversion',
            canon: { domain: 'conversion', status: 'converted' },
          }
        : DAY_CONVERSION_CHIP[conversion],
    );
  }
  // Payment-handoff derived values (HANDOFF LAW; the ONE shared derivation):
  //   recorded -> Payment recorded; accepted-and-unpaid closed visit ->
  //   Payment pending; advised-only closed visit -> Follow-up needed;
  //   plan-less closed rows keep the original honest 'No payment yet'.
  const handoff = derivePaymentHandoff(row);
  if (handoff.kind === 'recorded') {
    chips.push({
      label: 'Payment recorded',
      tone: 'positive',
      family: 'payment',
      canon: { domain: 'payment', status: 'recorded' },
    });
  } else if (handoff.kind === 'collect') {
    chips.push({
      label: 'Payment pending',
      tone: 'pending',
      family: 'payment',
      canon: { domain: 'payment', status: 'collect_pending' },
    });
  } else if (handoff.kind === 'advised_only') {
    chips.push({
      label: 'Follow-up needed',
      tone: 'info',
      family: 'payment',
      canon: { domain: 'followup', status: 'required' },
    });
  } else if (
    conversion === 'awaiting_decision' &&
    visitClosedForHandoff(row) &&
    row.status !== 'cancelled' &&
    row.status !== 'no_show'
  ) {
    // MONEY-COMPLETION-V1 (rider R-52, the proven read-side hole): an
    // advised-EVENT-only closed visit (no plan-item rows on the wire)
    // resolved awaiting_decision but the handoff chip -- which needs plan
    // COUNTS -- stayed silent, so NEITHER chip spoke and the row fell to
    // the plan-less "No payment yet" + quiet door (erin's shape). The
    // event-signal twin now renders IDENTICALLY to the plan-count twin --
    // same closed gate, same terminal-negative suppression (the
    // HANDOFF_SUPPRESSED_STATUSES pair, mirrored): one follow-up
    // vocabulary, one fact, one chip. Open rows and cancelled/no-show rows
    // keep today's render exactly.
    chips.push({
      label: 'Follow-up needed',
      tone: 'info',
      family: 'payment',
      canon: { domain: 'followup', status: 'required' },
    });
  } else if (
    (row.status === 'fulfilled' || row.status === 'completed') &&
    row.paymentRecorded === false
  ) {
    // R-56 (founder-pending vocabulary): the label stays this screen's
    // pinned copy; only the canon TONE home is consumed.
    chips.push({
      label: 'No payment yet',
      tone: 'neutral',
      family: 'payment',
      canon: { domain: 'payment', status: 'none' },
    });
  }
  // Cancelled-row reason (rider R-08): the recorded controlled token rendered
  // as its label -- display of a server-shipped fact, never invented; absent
  // on reason-less cancels (pre-apply and legacy rows stay exactly as today).
  if (
    row.status === 'cancelled' &&
    typeof row.cancelReasonCode === 'string' &&
    row.cancelReasonCode !== ''
  ) {
    chips.push({
      label: `Reason: ${cancelReasonLabel(row.cancelReasonCode)}`,
      tone: 'neutral',
      family: 'reason',
    });
  }
  return chips;
}

// --- D1: THE ONE STATE CELL (R-I DENSITY LAW) -------------------------------

/**
 * FRONT-OFFICE-DENSITY-PASS-V1 (D1). The board's Status and Ops-status columns
 * collapse into ONE State cell that renders at most TWO chips: the STORED
 * status (the machine state -- R-C keeps the vocabulary frozen, and
 * dayStatusChip stays the ONE label+tone home, byte-identical) plus the single
 * highest-precedence ops chip.
 *
 * PRECEDENCE (founder ruling 2026-07-29 D1): payment > conversion > visit >
 * reason -- money first, because a row that owes money is the row the desk must
 * act on; then the commercial outcome; then the visit state; then the cancel
 * reason. Read off the chip's own `family` tag, never its label.
 *
 * EVIDENCE-ROWS LAW (FLOW_183): nothing is deleted. Every chip beyond the cap
 * stays in `overflow`, the cell renders a "+N" affordance, and the row's
 * EXISTING drawer (LeadQuickViewHost for desk roles, DoctorQueueContextDrawer
 * for the doctor) is the one-click-deeper home -- no new surface, no new read.
 */
export const DAY_STATE_CHIP_CAP = 2;

/** The State-cell precedence order (D1). */
const DAY_OPS_FAMILY_ORDER: readonly DayOpsFamily[] = [
  'payment',
  'conversion',
  'visit',
  'reason',
];

export interface DayStateCell {
  /** The stored-status chip -- always present (byte-identical to today). */
  readonly status: DayStatusChip;
  /** The one ops chip that earns the second slot, or null when the row has none. */
  readonly primary: DayOpsChip | null;
  /** Ops chips rendered INLINE after `primary` (DR-22). Empty under the
   *  default cap, so every desk lens is byte-identical. */
  readonly inline: readonly DayOpsChip[];
  /** The remaining ops chips, in precedence order -- reachable, never deleted. */
  readonly overflow: readonly DayOpsChip[];
}

/**
 * How many OPS chips the State cell renders inline (the primary included).
 *
 * DEFAULT -- the D1 two-chip cap: ONE ops chip inline, the rest behind "+N".
 * Every desk lens keeps this, byte-identical.
 *
 * DOCTOR (DOCTOR-PRECALL-POLISH-V1, DR-22) -- ALL. The founder walk found the
 * doctor's own rows hiding their END STATES ("Visit finalized", "Payment
 * recorded") behind a "+1" the doctor must click to read. The desk's reason
 * for the cap is density under a wide action column; the doctor's row has one
 * action (the consult door) and its ops chips ARE the answer to "is this
 * patient done". So the doctor lens renders them inline and NO "+N" is
 * offered -- show what matters or nothing, never a counter.
 *
 * Nothing is hidden either way: the default path keeps the overflow door + its
 * title, and the doctor path has no overflow left to hide (EVIDENCE-ROWS LAW).
 */
export const DAY_STATE_OPS_INLINE_DEFAULT = DAY_STATE_CHIP_CAP - 1;
export const DAY_STATE_OPS_INLINE_ALL = Number.POSITIVE_INFINITY;

/** The inline allowance for one role -- THE one derivation both the cell and
 *  the render read (never two role tests that can drift). */
export function dayStateOpsInlineForRole(role: string | null | undefined): number {
  return role === 'doctor'
    ? DAY_STATE_OPS_INLINE_ALL
    : DAY_STATE_OPS_INLINE_DEFAULT;
}

/** The "+N" affordance's label. N is the overflow COUNT, never a guess. */
export function dayStateOverflowLabel(count: number): string {
  return `+${count}`;
}

/** The overflow affordance's title: names WHERE the hidden facts live, so the
 *  "+2" is never a dead end (EVIDENCE-ROWS law). */
export const DAY_STATE_OVERFLOW_TITLE =
  'More state on this row - open the row for the full detail';


/**
 * FRONT-DESK-CORRECTIVE-V1 (L-04) -- THE THIRD BRANCH'S DISCLOSURE.
 *
 * Three branches render the "+N". With a drawer it is a <button> that opens
 * the row. Without one it is a link IF dayStateOverflowHref resolves (needs
 * recordDoorOpen AND a linked patientId). Fail BOTH and it rendered an inert
 * <span> whose hidden labels lived only in a native `title` -- WHICH DOES NOT
 * EXIST ON TOUCH. Confirmed from field use, Dr. Lakshmi, 2026-09-03:
 * "When they click, it doesn't do anything."
 *
 * THE FIX IS THE ONE THE LAW ALREADY NAMES. The two-chip cap
 * (docs/design/design-language/07-BEHAVIOURAL-LAWS.md:478-480) reads: the
 * overflowed chips "stay in the DOM behind a NATIVE DISCLOSURE and remain
 * reachable in the row's existing drawer." This branch is precisely the one
 * with no drawer to reach them in, so the native disclosure is all there is
 * -- and it was the half never built. <details>/<summary> IS that element:
 * keyboard operable, touch operable, no JS, and it cannot be mistaken for a
 * door to a row that does not exist (AdoraCare standard s.6, "no dead ends").
 *
 * PRESERVATION: the "+N" count stays as the closed summary, the row's testid
 * is unchanged, and the title still names where the facts live. The other two
 * branches are BYTE-IDENTICAL -- this helper is reached only from the span
 * branch.
 */
export function dayStateOverflowDisclosure(
  overflow: readonly DayOpsChip[],
  rowId: string | number,
  countLabel: string,
): JSX.Element {
  return (
    <details>
      <summary
        className="day-board__state-more"
        data-testid={`day-board-state-more-${rowId}`}
      >
        {countLabel}
      </summary>
      {overflow.map((chip) => (
        <AcBadge key={chip.label} tone={chip.tone}>
          {chip.label}
        </AcBadge>
      ))}
    </details>
  );
}

/**
 * FRONT-DESK-DAY-BOARD-V1 (Partha TAB row "WALK-IN APPOINTMENTS"): the +N's
 * one-click-deeper home for a row with NO drawer. A walk-in born row
 * (leadId null) has no LeadQuickView, so its overflow facts used to ride a
 * title tooltip only -- a dead affordance. When the row IS linked to a
 * golden record AND the caller already holds the Patient-360 continuity
 * door on this board (the SAME recordDoorOpen gate -- role-shaped and
 * reachability-gated, LES-016), the +N renders as a link into that EXISTING
 * record surface, where the row's full detail lives. An unlinked drawer-less
 * row keeps the honest inert span (no backing read exists -- absence, never
 * a dead door). Rows with a drawer are BYTE-IDENTICAL (the drawer stays the
 * overflow home; this helper returns null for them).
 */
export function dayStateOverflowHref(
  role: string | null | undefined,
  row: Pick<TodayAppointment, 'leadId' | 'patientId'>,
  recordDoorOpen: boolean,
): string | null {
  if (rowOpenKind(role, row) !== 'none') return null;
  if (!recordDoorOpen) return null;
  if (typeof row.patientId !== 'string' || row.patientId === '') return null;
  return `/dashboard/patients/${row.patientId}`;
}

export function dayStateCell(
  row: TodayAppointment,
  orgLabels?: AppointmentStatusLabelOverrides | null,
  /** Inline ops-chip allowance (DR-22). Omitted = the D1 cap, so every
   *  existing caller and test is byte-identical. */
  opsInline: number = DAY_STATE_OPS_INLINE_DEFAULT,
): DayStateCell {
  const status = dayStatusChip(row.status, orgLabels);
  // THE one ops derivation, unchanged (dayOpsChips output is byte-identical
  // apart from the additive family tag) -- ranked, never re-derived.
  const ranked = [...dayOpsChips(row)].sort(
    (a, b) =>
      DAY_OPS_FAMILY_ORDER.indexOf(a.family) -
      DAY_OPS_FAMILY_ORDER.indexOf(b.family),
  );
  // At least the primary slot always applies -- an allowance below 1 can
  // never delete the highest-precedence chip.
  const opsSlots = Math.max(1, opsInline);
  return {
    status,
    primary: ranked.length > 0 ? (ranked[0] as DayOpsChip) : null,
    inline: ranked.slice(1, opsSlots),
    overflow: ranked.slice(opsSlots),
  };
}

// --- D3: WAITING TIME (by REUSE, one derivation) ----------------------------

/**
 * FRONT-OFFICE-DENSITY-PASS-V1 (D3). Whole minutes waited since the recorded
 * arrival, by REUSE of THE ONE deterministic derivation (waitingMinutes,
 * patient-360-view.ts) and gated EXACTLY as Patient 360 gates it -- rendered
 * only while the patient is actually waiting (arrived | checked_in). A second
 * derivation is forbidden; `nowMs` is injected, so nothing reads a clock during
 * render and the value is deterministic in tests.
 */
export function dayBoardIsWaiting(
  row: Pick<TodayAppointment, 'status'> & { readonly visitStatus?: string | null; readonly consultStarted?: boolean | null },
): boolean {
  return (row.status === 'arrived' || row.status === 'checked_in') &&
    row.visitStatus !== 'finalized' && !doctorConsultOpen(row);
}

export function dayBoardWaitingMinutes(
  row: Pick<TodayAppointment, 'arrivedAt' | 'status'> & { readonly visitStatus?: string | null; readonly consultStarted?: boolean | null },
  nowMs: number,
): number | null {
  const waited = waitingMinutes(row.arrivedAt ?? null, nowMs);
  if (waited === null) return null;
  return dayBoardIsWaiting(row) ? waited : null;
}

/** The waiting line's copy (zone-free: a DURATION, not a timestamp). */
export function dayBoardWaitingLabel(minutes: number): string {
  // T7 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1, founder ruling 2026-08-15): THE
  // ONE duration grammar (formatDurationHm -- "12m" / "9h 38m", never a
  // raw "578 min"), the same grammar the doctor surface's waiting chip
  // speaks; the hand-built `${minutes} min` interpolation was the exact
  // second-grammar class #557 F6a retired.
  return `Waiting ${formatDurationHm(minutes)}`;
}

// --- FRONT-OFFICE-DAY-BOARD-TRUTH-AND-WAIT-CLOCK-V1 -------------------------
//
// THREE display-only additions. Every input is a server-shipped fact; this
// module derives no identity, no money and no timestamp -- it formats what the
// read model already resolved (the board "renders what it is given").

/**
 * The linked golden record's UMR for a board row, or null.
 *
 * An UNLINKED row returns null and the surface renders NOTHING -- never a
 * placeholder, never a dash, never an invented identifier. Whitespace-only
 * values are treated as absent (the server already trims; this is the
 * defensive twin, the LES-057 absent-to-null posture).
 */
export function dayBoardUmr(
  row: Pick<TodayAppointment, 'linkedPatientUmr'>,
): string | null {
  const umr = row.linkedPatientUmr?.trim();
  return umr !== undefined && umr !== '' ? umr : null;
}

/** The UMR's dense inline label. REUSES the established identity-line idiom
 *  (`UMR AC-000123`, doctor-consult-view.ts) so the same patient reads the
 *  same way at the desk and in the consult banner -- one vocabulary. */
export function dayBoardUmrLabel(umr: string): string {
  return `UMR ${umr}`;
}

/**
 * PDC-FO-GAP-CLOSURE-V1 (G-NEW, founder ruling 2026-08-14) -- THE ONE
 * new/returning badge owner. Both render sites (the front-desk day-board row
 * and the doctor chair card) consume THIS derivation; a second copy map is
 * forbidden (the dayStatusChip one-label-home discipline).
 *
 * The FACT is server-derived from the golden record
 * (TodayAppointment.linkedPatientPriorVisit): 'returning' = at least one
 * prior appointment that REACHED the clinic (the touch family -- stored
 * arrived / checked_in / completed / fulfilled: Arrived, Checked-in,
 * Waiting, Consult-started, Completed all count; founder ruling 2026-08-15,
 * SHIPPED-WORK-TRUTH-CORRECTIVE-V1 T1, superseding the close-family
 * reading); 'new' = none -- a cancelled-only or no-show-only history is
 * still "New patient". NULL -- an unlinked row or an unproven history --
 * renders NOTHING: never a guessed badge, never a placeholder (the
 * dayBoardUmr absence posture).
 *
 * TONES (AcBadge semantic tones, design-language 02): 'info' for the first
 * visit -- the state the desk and the doctor should notice -- and 'neutral'
 * for the ordinary returning case. Identity never borrows STATUS colors;
 * this is a visit-history fact rendered in the semantic pair, not a stage.
 */
/** Selected-view visit-history count; absent/unknown history is never new. */
export function returningVisitCount(
  rows: readonly Pick<TodayAppointment, 'linkedPatientPriorVisit'>[] | null,
): { value: string; explanation: string } {
  if (rows === null) return { value: 'Unavailable', explanation: 'Visit history unavailable for this selection' };
  const known = rows.filter(row => row.linkedPatientPriorVisit === 'returning').length;
  const unknown = rows.filter(row => row.linkedPatientPriorVisit !== 'returning' && row.linkedPatientPriorVisit !== 'new').length;
  return {
    value: String(known),
    explanation: unknown > 0
      ? `${known} verified returning ${known === 1 ? 'visit' : 'visits'}; ${unknown} ${unknown === 1 ? 'row with' : 'rows with'} unknown history omitted`
      : `${known} returning ${known === 1 ? 'visit' : 'visits'} in this selection`,
  };
}

export function dayBoardNewReturningBadge(
  row: Pick<TodayAppointment, 'linkedPatientPriorVisit'>,
): { label: string; tone: AcBadgeTone } | null {
  const fact = row.linkedPatientPriorVisit;
  if (fact === 'new') return { label: 'New patient', tone: 'info' };
  if (fact === 'returning') return { label: 'Returning patient', tone: 'neutral' };
  return null;
}

/**
 * The row's OUTSTANDING obligation in minor units, or null.
 *
 * DISPLAY-ONLY: the number is the server's, from THE ONE netting derivation
 * (lib/billing-queue.ts). This function performs NO payment arithmetic -- a
 * second due calculation is forbidden. It only refuses to render a value that
 * is not a positive obligation, so a zero or a settled episode shows nothing
 * (no noisy zero chips) and a non-audience role -- whose payload omits the
 * key entirely -- shows nothing by construction.
 */
export function dayBoardDueMinorUnits(
  row: Pick<TodayAppointment, 'outstandingMinorUnits'>,
): number | null {
  const due = row.outstandingMinorUnits;
  return typeof due === 'number' && Number.isFinite(due) && due > 0 ? due : null;
}

/** The due field's label. FIELDS-NOT-SENTENCES: a labeled money field through
 *  THE ONE minor-units formatter, never prose and never a re-derived amount. */
export function dayBoardDueLabel(minorUnits: number): string {
  return `Due ${formatMinorUnitsInr(minorUnits)}`;
}

/**
 * RECEPTION-MONEY-TRUTH-V1 (A2): the repeat-visit CARRIED BALANCE, or null.
 * DISPLAY-ONLY like the due field above -- the number is the server's, from
 * THE patient-grain money owner (lib/patient-money.ts); no arithmetic here.
 * Null / absent / non-positive renders NOTHING (a first visit or a settled
 * history never gets a zero chip), and a non-audience payload omits the key
 * so the doctor's board shows nothing by construction.
 */
export function dayBoardCarriedMinorUnits(
  row: Pick<TodayAppointment, 'carriedForwardMinorUnits'>,
): number | null {
  const carried = row.carriedForwardMinorUnits;
  return typeof carried === 'number' && Number.isFinite(carried) && carried > 0
    ? carried
    : null;
}

/** The carried field's label (FIELDS-NOT-SENTENCES; THE ONE formatter). */
export function dayBoardCarriedLabel(minorUnits: number): string {
  return `Carried ${formatMinorUnitsInr(minorUnits)}`;
}

/** A2: the row-grain "payment happened BEFORE" fact -- the founder's exact
 *  repeat-visit question. Same display-only discipline as Due/Carried. */
export function dayBoardPriorPaidMinorUnits(
  row: Pick<TodayAppointment, 'priorPaidMinorUnits'>,
): number | null {
  const paid = row.priorPaidMinorUnits;
  return typeof paid === 'number' && Number.isFinite(paid) && paid > 0
    ? paid
    : null;
}

export function dayBoardPriorPaidLabel(minorUnits: number): string {
  return `Paid before ${formatMinorUnitsInr(minorUnits)}`;
}

/**
 * FOUNDER RULING R3 (2026-07-31): the wait clock renders ONLY for rows at
 * ARRIVED OR LATER -- the patient is in the clinic and the elapsed time is a
 * WAIT the desk can act on. The eleven-label journey's arrived-or-later
 * display states (Arrived, Intake pending, With doctor, Consultation done,
 * Billing pending) map onto exactly these three STORED statuses: `arrived`,
 * `checked_in` (which carries both Intake pending and With doctor) and
 * `fulfilled` (which carries Consultation done and Billing pending). This is
 * the GATE, and it is an ALLOW-LIST on purpose: a pre-arrival status, a
 * terminal status and an unknown stored token all fall through to no clock,
 * so the surface fails closed by construction rather than by exclusion
 * bookkeeping.
 *
 * `completed` is deliberately absent even though it is "later" than arrived:
 * it is a TERMINAL state and terminal rows stay clockless as shipped.
 */
export const DAY_STAGE_CLOCK_ARRIVED_OR_LATER_STATUSES: readonly string[] = [
  'arrived',
  'checked_in',
  'fulfilled',
];

/**
 * PRE-ARRIVAL stored statuses, named explicitly so R3's intent is legible at
 * the call site and pinned in the suite. A booked or confirmed row has not
 * reached the clinic, so its stage age is BOOKING AGE, not a wait -- and on a
 * Today board most such rows were booked days earlier, which is exactly the
 * noise this ruling removes. Not a second gate: the allow-list above already
 * excludes every one of these.
 */
export const DAY_STAGE_CLOCK_PRE_ARRIVAL_STATUSES: readonly string[] = [
  'waitlist',
  'booked',
  'confirmed',
];

/**
 * TERMINAL stored statuses for the wait clock: a row that has finished, been
 * refused or been moved is not WAITING in a stage, so it never carries a
 * clock (package rule, verbatim: Completed / No-show / Cancelled /
 * Rescheduled). Deliberately NOT the handoff-suppression pair and NOT the
 * canonical-status set -- this is the clock's own named list. Retained after
 * R3 as the pinned statement of that rule; the allow-list above is what
 * actually excludes these (the two are proven DISJOINT in the suite).
 */
export const DAY_STAGE_CLOCK_TERMINAL_STATUSES: readonly string[] = [
  'completed',
  'no_show',
  'cancelled',
  'rescheduled',
];

/**
 * Whole minutes the row has been in its CURRENT stage, or null.
 *
 * FAIL-CLOSED at every step -- a missing, unparsable or future entry moment
 * renders NO clock. Nothing here estimates: the anchor is the server's
 * RECORDED currentStatusSince (the matching transition event's occurred_at),
 * never appointmentTime and never arrivedAt, so a later stage's clock can
 * never be anchored at the booking. A stage transition writes a new entry
 * moment, which resets the elapsed value by construction.
 *
 * STAGE SCOPE (FOUNDER RULING R3): arrived-or-later only. A pre-arrival row
 * (waitlist / booked / confirmed), a terminal row and an unknown stored token
 * all render NO clock.
 *
 * ONE FACT, ONE PLACE (FOUNDER RULING R1, the arrived exception): on an
 * `arrived` row the D3 waiting line in the Time cell ALREADY renders this
 * exact elapsed value measured from the same arrival event -- that line IS the
 * arrived clock -- so the chip stands down rather than print the same minutes
 * twice. The two surviving stages measure something the Time cell does not
 * speak (time in the chair, time since the visit closed), so both render and
 * neither repeats the other.
 *
 * `nowMs` is INJECTED (the D3 precedent): nothing reads a clock during render
 * and the value is deterministic in tests.
 */
export function dayBoardStageElapsedMinutes(
  row: Pick<TodayAppointment, 'status' | 'currentStatusSince'>,
  nowMs: number,
): number | null {
  // R3: the ONE gate -- arrived or later, nothing else.
  if (!DAY_STAGE_CLOCK_ARRIVED_OR_LATER_STATUSES.includes(row.status)) return null;
  // R1: the arrived row's clock is the D3 waiting line, not this chip.
  if (row.status === 'arrived') return null;
  const since = row.currentStatusSince;
  if (typeof since !== 'string' || since === '') return null;
  const t = Date.parse(since);
  if (Number.isNaN(t)) return null;
  const minutes = Math.floor((nowMs - t) / 60000);
  return minutes >= 0 ? minutes : null;
}

/**
 * The clock's TIME-ONLY label: "47m" under an hour, then "1h 12m". The stage
 * NAME is never restated, renamed or duplicated -- the status chip beside it
 * is the one stage vocabulary.
 *
 * The third rung ("3d") exists because the Today board legitimately carries
 * rows whose current stage began days ago (a `booked` row is usually booked
 * days before the appointment), and "121h 30m" is unreadable. It is a
 * READABILITY rung on the same true number, never a different measurement,
 * and it matches the compact age ladder already shipped on the leakage
 * surface (pinned in the suite so the product has ONE duration grammar).
 */
export function dayBoardStageElapsedLabel(minutes: number): string {
  // DOCTOR-HOME-STATE-MODEL-V1 (F6a): the ladder itself was HOISTED to the
  // ONE duration owner (formatDurationHm, lib/datetime-ist.ts) -- this label
  // keeps its name and callers and delegates, byte-identical output.
  return formatDurationHm(minutes);
}

/** The clock chip's title: names WHAT is being measured, since the chip
 *  itself is deliberately time-only. No threshold language -- escalation
 *  thresholds are a future config-backed package and the seven-color law is
 *  untouched (this chip is always the neutral tone). */
export const DAY_STAGE_CLOCK_TITLE = 'Time in the current stage';

// --- Day actions (EXISTING manage paths only; REUSE LAW) -------------------

/** The one-press day hops the board offers. `not_converted` (outcome
 *  authority coc/doctor -- the clinical surfaces own it) and `reschedule`
 *  (the workbench's explicit slot-grid flow) are DELIBERATELY absent. */
export type DayBoardActionKind = Exclude<
  WorkbenchActionKind,
  'not_converted' | 'reschedule'
>;

export function dayBoardActions(status: string): readonly DayBoardActionKind[] {
  return allowedWorkbenchActions(status).filter(
    (a): a is DayBoardActionKind => a !== 'not_converted' && a !== 'reschedule' && a !== 'fulfilled' && a !== 'confirm',
  );
}

/** Compact board and Reception quick-view labels; shared workbench copy is unchanged. */
export const DAY_CONFIRM_SHORT_COPY = 'Confirm';

const ACTION_COPY: Readonly<Record<DayBoardActionKind, string>> = {
  confirm: DAY_CONFIRM_SHORT_COPY,
  arrived: ARRIVED_BUTTON_COPY,
  checked_in: CHECKED_IN_BUTTON_COPY,
  fulfilled: FULFILLED_BUTTON_COPY,
  cancel: CANCEL_BUTTON_COPY,
  no_show: NO_SHOW_BUTTON_COPY,
};

export function dayBoardActionLabel(action: DayBoardActionKind, compactReception = false): string {
  return compactReception && action === 'checked_in' ? 'Check in' : ACTION_COPY[action];
}

const ACTION_SUCCESS: Readonly<Record<DayBoardActionKind, string>> = {
  confirm: CONFIRM_SUCCESS_COPY,
  arrived: ARRIVED_SUCCESS_COPY,
  checked_in: CHECKED_IN_SUCCESS_COPY,
  fulfilled: FULFILLED_SUCCESS_COPY,
  cancel: CANCEL_SUCCESS_COPY,
  no_show: NO_SHOW_SUCCESS_COPY,
};

/** The forward hop renders as the row's ONE primary action (visual bar:
 *  one primary per row); the rest are quiet secondaries. */
const PRIMARY_ACTION_BY_STATUS: Readonly<Record<string, DayBoardActionKind>> = {
  booked: 'arrived',
  confirmed: 'arrived',
  arrived: 'checked_in',
};

// --- D2: ONE NEXT ACTION (the P7 reference machine) -------------------------

/**
 * FRONT-OFFICE-DENSITY-PASS-V1 (D2). The row's action lens -- everything the
 * next-action machine needs, all of it ALREADY derived by the board's existing
 * pure helpers. Passed in so the machine stays pure and testable, exactly as the
 * P7 reference machine (PrimaryAction, CallcenterCommandCenter.tsx) takes its
 * affordance state as props.
 */
export interface DayBoardActionLens {
  /** The action lens (canActOnDayBoard) -- false = zero hops, zero overflow. */
  readonly canAct: boolean;
  /** The money handoff owns the row's primary (checkoutHandoff !== null); the
   *  Collect CTA itself is rendered by the host's payment slot. */
  readonly collectIsPrimary?: boolean;
  /** The intake door owns the row's primary (intakeRowPlan). */
  readonly intakeIsPrimary?: boolean;
  /** The intake plan suppresses the stored check-in hop (intakeRowPlan). */
  readonly suppressCheckedIn?: boolean;
}

export interface DayBoardNextAction {
  /** The ONE primary hop, or null when another affordance (Collect / intake)
   *  owns the primary, when the row is terminal, or on a view-only lens. */
  readonly primary: DayBoardActionKind | null;
  /** At most ONE visible secondary (P12: one primary and one visible
   *  secondary per row; everything else is quiet). */
  readonly secondary: DayBoardActionKind | null;
  /** The rest -- rendered behind the row's overflow disclosure, ACTION LENS
   *  ONLY. Nothing is deleted; a hop stays one click away. */
  readonly overflow: readonly DayBoardActionKind[];
}

/**
 * THE one next-action machine for a board row (D2), modelled on the P7
 * reference machine (PrimaryAction, CallcenterCommandCenter.tsx:438-492): one
 * primary, quiet secondaries, and an honest verdict rather than a wall of
 * equal-weight buttons.
 *
 * Verdicts (pure; the view renders them):
 *   - The FORWARD hop (PRIMARY_ACTION_BY_STATUS) is the primary -- it is what
 *     the desk is actually there to do.
 *   - When the Collect CTA or the intake door owns the primary, the forward hop
 *     DEMOTES to the visible secondary (the pre-existing "demotes to a ghost"
 *     rule, now with a named slot) -- one primary per row, never a wall.
 *   - Every remaining hop keeps dayBoardActions order and falls to overflow.
 *   - A view-only lens gets NOTHING (denial is absence, never a disabled
 *     control), and a terminal row's empty hop set stays empty.
 *
 * dayBoardActions verdicts are UNCHANGED -- this machine only ranks them.
 */
export function dayBoardNextAction(
  row: Pick<TodayAppointment, 'status'>,
  lens: DayBoardActionLens,
): DayBoardNextAction {
  if (!lens.canAct) return { primary: null, secondary: null, overflow: [] };
  const hops = dayBoardActions(row.status).filter(
    (a) => !(lens.suppressCheckedIn === true && a === 'checked_in'),
  );
  const forward = PRIMARY_ACTION_BY_STATUS[row.status];
  const forwardOffered =
    forward !== undefined && hops.includes(forward) ? forward : null;
  const otherOwnsPrimary =
    lens.collectIsPrimary === true || lens.intakeIsPrimary === true;
  const primary = otherOwnsPrimary ? null : forwardOffered;
  // The demoted forward hop leads the quiet queue (it is still the most likely
  // next act); the rest keep the existing dayBoardActions order.
  const rest: DayBoardActionKind[] = [
    ...(primary === null && forwardOffered !== null ? [forwardOffered] : []),
    ...hops.filter((a) => a !== primary && a !== forwardOffered),
  ];
  return {
    primary,
    secondary: rest.length > 0 ? (rest[0] as DayBoardActionKind) : null,
    overflow: rest.slice(1),
  };
}

/** The row overflow disclosure's label (D2). Count is real, never a guess. */
export function dayActionOverflowLabel(count: number): string {
  return `${count} more`;
}

/**
 * FRONTDESK-CHECKIN-AND-IDENTITY-V1: where one board action ROUTES (pure --
 * the container executes the verdict):
 *   - 'identity_step' -- the check-in moment (arrived / checked-in hop) on a
 *     lead row with NO golden-record link: register-or-link FIRST (Scope 1a).
 *     Already-linked rows and lead-less rows NEVER route here -- the
 *     byte-identical fast path (Scope 1d pin).
 *   - 'cancel_dialog' -- cancel opens the reason picker (rider R-08).
 *   - 'window_confirm' -- the remaining destructive hop (no-show) keeps its
 *     confirm.
 *   - 'direct' -- everything else fires the existing mutation unchanged.
 */
export type DayBoardActionRoute =
  | 'identity_step'
  | 'cancel_dialog'
  | 'fulfill_dialog'
  | 'no_show_dialog'
  | 'direct';

/** Returning-patient check-in (Ravi, 16 Oct 2026): an arrived visit whose
 * linked patient already reached a clinic on an earlier day (the board's own
 * Returning badge) and has no registration window in progress. Selecting
 * that named row and confirming is the identity check; the server re-verifies
 * the earlier visit before older blank fields stop gating. */
export function isReturningCheckInRow(row: TodayAppointment): boolean {
  return row.status === 'arrived' && row.hasPatientRecord === true &&
    row.linkedPatientPriorVisit === 'returning' && row.intakeStatus !== 'in_progress';
}

export function dayBoardActionRoute(
  row: Pick<TodayAppointment, 'leadId' | 'patientId' | 'hasPatientRecord' | 'registrationCaptured'>,
  action: DayBoardActionKind,
  /** Arrival stays immediate with the spine on. Check-in always respects the
   *  server's registration gate, regardless of that presentation flag. */
  opts?: { readonly intakeSpineEnabled?: boolean; readonly fulfillmentConfirmationEnabled?: boolean },
): DayBoardActionRoute {
  if (
    (action === 'checked_in' || (action === 'arrived' && opts?.intakeSpineEnabled !== true)) &&
    row.leadId != null &&
    row.hasPatientRecord !== true &&
    row.registrationCaptured !== true
  ) {
    return 'identity_step';
  }
  if (action === 'cancel') return 'cancel_dialog';
  if (action === 'no_show') return 'no_show_dialog';
  // HS04 applies to Reception HOME only. The appointment workbench and
  // non-Home consumers retain their own existing action contracts.
  if (action === 'fulfilled' && opts?.fulfillmentConfirmationEnabled === true) {
    return 'fulfill_dialog';
  }
  return 'direct';
}

/** The Home confirmation must accept exactly the two origins already allowed
 * by APPOINTMENT_ACTION_ALLOWED_FROM.fulfilled, but never a changed target. */
export function sameFulfillmentTarget(
  held: Pick<TodayAppointment, 'status' | 'patientId' | 'appointmentTime' | 'clinicId'>,
  current: Pick<TodayAppointment, 'status' | 'patientId' | 'appointmentTime' | 'clinicId'> | undefined,
): boolean {
  return current !== undefined &&
    (current.status === 'arrived' || current.status === 'checked_in') &&
    current.status === held.status &&
    current.patientId === held.patientId &&
    current.appointmentTime === held.appointmentTime &&
    current.clinicId === held.clinicId;
}

// --- Summary line (SCOPE 1(c) ruling) --------------------------------------

/** Statuses that count as an OP (out-patient) seen so far today. */
const OP_STATUSES: readonly string[] = [
  'arrived',
  'checked_in',
  'fulfilled',
  'completed',
];

/** "OP so far today" -- real, from the SAME day rows (one computation). */
export function opSoFarCount(rows: readonly TodayAppointment[]): number {
  return rows.filter((r) => OP_STATUSES.includes(r.status)).length;
}

/** SCOPE 3 (arrival cascade, H2 glance contract): the NEXT EXPECTED patient
 *  = the earliest still-expected (booked/confirmed) row. Rows arrive
 *  time-ascending from the read, so the first match wins. Null when nobody
 *  is left to arrive. */
export function nextExpectedRowId(
  rows: readonly TodayAppointment[],
): number | null {
  const next = rows.find((r) => r.status === 'booked' || r.status === 'confirmed');
  return next ? next.id : null;
}

/** SEAM-3 (recorded): no clinic-day collections read exists; an
 *  appointment-linked sum would dishonestly undercount. The line names the
 *  seam and points at the billing home instead of inventing a number. */
/** Adversarial-review F7: the day read caps at this many rows (earliest
 *  win). Exported so every day surface that renders the SAME capped read
 *  states the SAME honesty line -- never a second cap number, never a
 *  silently undercounted day. APPOINTMENT-BOOK-V1 is the second consumer
 *  (the planning calendar reads the identical cap). */
export const DAY_READ_ROW_CAP = 100;

/** The row-cap honesty line. ONE copy owner for every consumer of the capped
 *  day read (the board below and the planning calendar). */
export const DAY_READ_TRUNCATED_COPY =
  'Showing the first 100 rows of the day - later appointments are not listed here.';

// RECEPTION-OS-BUILD-V1 (F-16 resolved by ruling Q-02=A, 2026-08-03): the
// day's collections TOTAL renders on the Money page ONLY -- this seam now
// points at the door the role actually has on its nav (FO-1 removed the
// old Billing-entry advertisement it used to name). Still TEXT, never a
// tappable control (SEAM law); no second render of the figure exists.
export const COLLECTIONS_SEAM_COPY = 'Collections today: see Money';

/** The view-only honesty line. ONE copy owner: the board's own `full` chrome
 *  renders it, and a host that supplies its own head (chrome 'embedded')
 *  renders it from HERE rather than restating it -- FRONT-OFFICE-DENSITY-PASS-V1
 *  D4, the DAY_READ_TRUNCATED_COPY precedent. */
export const DAY_BOARD_VIEW_ONLY_COPY =
  'View only - actions stay with the front desk.';

/** D3 (RECEPTION-WALK-CORRECTIVE-V1): the DAY-scoped action-denial line -- an
 *  action-capable role (the front desk) viewing a past/future board. DISTINCT
 *  from the ROLE-scoped view-only line above: here the remedy is "press Today",
 *  not "the front desk owns it", so a receptionist on the wrong day never reads
 *  the misleading role message. ONE copy owner: the board renders it in full
 *  chrome and the walk-in host renders it from THIS constant when embedded
 *  (the DAY_BOARD_VIEW_ONLY_COPY precedent). */
export const DAY_BOARD_ACTIONS_TODAY_ONLY_COPY =
  "Actions available on today's board only - press Today.";

// --- Pure view --------------------------------------------------------------

export interface DayBoardFeedback {
  readonly tone: 'success' | 'error';
  readonly text: string;
}

export interface ClinicDayBoardViewProps {
  readonly rows: readonly TodayAppointment[];
  readonly role: string | null;
  /** True while the caller's fetch is in flight (skeleton). */
  readonly loading: boolean;
  /** True when the caller's fetch failed (honest error, no invented rows). */
  readonly error: boolean;
  readonly feedback: DayBoardFeedback | null;
  /** The row id with a mutation in flight ('' = none). */
  readonly busyRowId: number | null;
  readonly onAction: (row: TodayAppointment, action: DayBoardActionKind) => void;
  readonly contextDisabled?: boolean;
  readonly onOpenRow: (row: TodayAppointment) => void;
  readonly onOpenPatientRecord?: (row: TodayAppointment) => void;
  /** SCOPE 5 slot: the payment door for a row (null = none). */
  readonly paymentAction?: (row: TodayAppointment) => ReactNode;
  /** SCOPE 4 slot: the case-sheet door for a row (doctor lens; null = none). */
  readonly caseSheetAction?: (row: TodayAppointment) => ReactNode;
  /** X27 (Lane 3, 2026-09-10) slot: the audited contact-reveal door for a
   *  row (PATIENT_MASTER_ROLES lens; the host supplies it only for a role
   *  it authorizes -- absent = no door, byte-identical board). */
  readonly contactRevealAction?: (row: TodayAppointment) => ReactNode;
  /** DOCTOR-CLINICAL-WORKSPACE-V1: 'embedded' skips the board's own header
   *  block so a host section (e.g. the doctor home's Today's queue shell)
   *  provides the heading -- ONE table implementation, never a second queue
   *  (REUSE LAW). Default 'full' = byte-identical to the pre-existing board. */
  readonly chrome?: 'full' | 'embedded';
  /** Home-only density: one status, remaining recorded facts in Details. */
  readonly compactHome?: boolean;
  /** PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-CHECKIN-SPINE-V1: the intake-
   *  spine lens (absent/false = byte-identical board). */
  readonly intakeSpineEnabled?: boolean;
  /** Opens the intake drawer for a row (the container hosts it). */
  readonly onIntake?: (row: TodayAppointment) => void;
  /** SEP15 vertical: the Patient 360 continuity door's UX gate -- the host
   *  passes { enabled: me.patientMasterEnabled === true } so the door only
   *  renders when the backing record surface is actually reachable
   *  (LES-016: never a dead door). Absent/false = no door (byte-identical
   *  board). */
  readonly patientRecordDoor?: { readonly enabled: boolean };
  /** DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: the clinic's ACTIVE roster
   *  (data.clinicDoctors off the board read). Feeds the assign picker;
   *  absent = no assign affordance renders (a host that does not pass the
   *  roster keeps the pre-vertical board byte-identical). */
  readonly clinicDoctors?: readonly ClinicRosterDoctor[];
  /** Opens the assign-doctor picker for a row (the container hosts it).
   *  Renders only for the action lens on an assignable-status row. */
  readonly onAssignDoctor?: (row: TodayAppointment) => void;
  /** FRONT-OFFICE-DENSITY-PASS-V1 (D3): the injected clock for the waiting-time
   *  line. ABSENT = no waiting line (byte-identical board for every host that
   *  does not pass it); nothing here ever reads a clock during render. */
  readonly nowMs?: number;
  /** RECEPTION-OS-BUILD-V1 (F-09/F-12): the org's display-label overrides for
   *  appointment statuses (`appointment.status_labels` off /me). ABSENT = the
   *  StatusChip canon defaults; the STORED vocabulary is never changed. */
  readonly statusLabels?: AppointmentStatusLabelOverrides | null;
  /**
   * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 -- WHICH DAY these rows belong to.
   * ABSENT / 'today' = byte-identical to the pre-package board for every host
   * (the doctor home, the COC clinic status page and the manager cockpit all
   * pass nothing and are untouched).
   *
   * A6 TRUTH LAW, enforced in ONE place here rather than per host:
   *  - 'past'   final recorded truth, READ ONLY. Denial stays ABSENCE (the
   *             board's standing rule): no action strip, no payment door, no
   *             case-sheet door, no assign door, no intake door -- never a
   *             disabled control. Recorded facts (the arrival moment, the
   *             stored states, cancelled / no-show rows) render in full.
   *  - 'future' the planned schedule only -- the same absence, plus no
   *             invented arrival, registration, clinical or payment state.
   *  - both     NO LIVE CLOCK: the waiting line, the stage clock and the
   *             "Next" marker are now-relative facts, so they are structurally
   *             withheld off today rather than printed against a stale now.
   *
   * This is a READ-SIDE lens: no mutation function, no route, no fence and no
   * server contract changes with it. The server remains the authority -- a
   * write attempted by any other means is refused exactly as it is today.
   */
  readonly dayScope?: DayScopeKind;
  /** SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T3, founder ruling 2026-08-15): the
   *  appointment id a search landing asked the board to OPEN ON -- the row
   *  gets the found emphasis and is scrolled into view once. PRESENTATION
   *  ONLY: no row logic, no action, no fence moves with it. Absent/null or
   *  an id not on this day's rows = byte-identical board. */
  readonly highlightAppointmentId?: number | null;
  /** A dock opened from a visible row must preserve the queue scroll. */
  readonly scrollHighlightIntoView?: boolean;
}

export function ClinicDayBoardView({
  rows,
  role,
  loading,
  error,
  feedback,
  busyRowId,
  onAction,
  onOpenRow,
  onOpenPatientRecord,
  contextDisabled = false,
  paymentAction,
  caseSheetAction,
  contactRevealAction,
  chrome = 'full',
  compactHome = false,
  intakeSpineEnabled,
  onIntake,
  patientRecordDoor,
  clinicDoctors,
  onAssignDoctor,
  nowMs,
  statusLabels,
  dayScope,
  highlightAppointmentId,
  scrollHighlightIntoView = true,
}: ClinicDayBoardViewProps): JSX.Element {
  // T3 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1): scroll the searched-for row into
  // view ONCE when it is on this day's rows. An effect (never a render-time
  // DOM read), so the SSR pin renders and the class below stays the only
  // render-path delta; an absent row is a silent no-op (the board never
  // breaks on a stale link).
  const highlightOnRows =
    typeof highlightAppointmentId === 'number' &&
    rows.some((r) => r.id === highlightAppointmentId);
  useEffect(() => {
    if (!highlightOnRows || !scrollHighlightIntoView) return;
    if (typeof document === 'undefined') return;
    const el = document.querySelector(
      `[data-testid="day-board-row-${highlightAppointmentId}"]`,
    );
    if (el !== null && 'scrollIntoView' in el) {
      (el as HTMLElement).scrollIntoView({ block: 'center' });
    }
  }, [highlightOnRows, highlightAppointmentId, scrollHighlightIntoView]);
  // DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1: ONE place decides what a non-today
  // day may render. Absent = 'today' = the pre-package board, byte-identical.
  const scope: DayScopeKind = dayScope ?? 'today';
  const isToday = scope === 'today';
  const readOnlyCopy = scope === 'past' && paymentAction ? 'Past appointments. Eligible payments are collected on the current payment day.' : dayScopeReadOnlyCopy(scope);
  // A6: the action lens closes off today. Denial is ABSENCE, never a disabled
  // control -- the board's standing rule, reused rather than restated.
  // D3: the two denial reasons are DISTINCT -- a wrong ROLE (the front desk
  // owns actions) vs a wrong DAY (actions live on today's board only). canAct
  // still gates the strip; the denial COPY below names WHICH reason applies, so
  // a receptionist on a past/future day never reads the role message.
  const roleCanAct = canActOnDayBoard(role);
  const canAct = roleCanAct && isToday;
  const op = opSoFarCount(rows);
  // NOW-RELATIVE FACTS are withheld off today: the "Next expected" marker and
  // the two elapsed clocks would otherwise measure a stale day against the
  // live clock and print a number that is simply false.
  const nextId = isToday ? nextExpectedRowId(rows) : null;
  const clockMs = isToday ? nowMs : undefined;
  const emptyCopy = dayScopeEmptyCopy(scope);
  // SEP15: per-state counts from the SAME rows the table renders (counts ==
  // list by construction; one derivation, zero second reads).
  const counts = dayStateCounts(rows, statusLabels);
  const recordDoorOpen =
    patientRecordDoor?.enabled === true && canOpenPatientRecordFromBoard(role);

  return (
    <div className="day-board" data-testid="clinic-day-board">
      {chrome === 'full' ? (
        <div className="day-board__head">
          <p className="day-board__eyebrow">Clinic day</p>
          {/* The title and the OP line are TODAY-SHAPED facts ("so far"
              describes a day in progress). Off today the title drops the word
              and the running total is withheld rather than relabelled -- the
              per-state counts below carry the honest tally for any day. */}
          <h2 className="day-board__title">
            {isToday ? "Today's appointments" : 'Appointments'}
          </h2>
          <p className="day-board__summary" data-testid="day-board-summary">
            {isToday ? (
              <span>
                OP so far today: <strong>{loading || error ? '-' : op}</strong>
              </span>
            ) : null}
            <span className="day-board__seam">{COLLECTIONS_SEAM_COPY}</span>
          </p>
          {readOnlyCopy !== null ? (
            <p className="day-board__viewonly" data-testid="day-board-dayscope">
              {readOnlyCopy}
            </p>
          ) : null}
          {/* SEP15: per-state counts -- derived from the SAME rows the list
              renders, so the header can never disagree with the list
              (counts == list by construction). Only today's states render;
              an empty day keeps the honest empty state below. */}
          {!loading && !error && counts.length > 0 ? (
            <p className="day-board__counts" data-testid="day-board-counts">
              {counts.map((c) => (
                <span
                  key={c.status}
                  className="day-board__count"
                  data-testid={`day-board-count-${c.status}`}
                >
                  {c.label} <strong>{c.count}</strong>
                </span>
              ))}
            </p>
          ) : null}
          {/* Adversarial-review F7: the read caps at 100 rows (earliest win) --
              say so instead of silently undercounting a huge day. */}
          {!loading && !error && rows.length >= DAY_READ_ROW_CAP ? (
            <p className="day-board__viewonly" data-testid="day-board-truncated">
              {DAY_READ_TRUNCATED_COPY}
            </p>
          ) : null}
          {/* D3: explicit inline action-denial, reason-distinguished, never
              silent absence. Role denial (the front desk owns actions) takes
              precedence; an action-capable role on a past/future day reads the
              press-Today remedy instead of the misleading role line. */}
          {!roleCanAct ? (
            <p className="day-board__viewonly" data-testid="day-board-viewonly">
              {DAY_BOARD_VIEW_ONLY_COPY}
            </p>
          ) : !isToday ? (
            <p
              className="day-board__viewonly"
              data-testid="day-board-actions-today-only"
            >
              {scope === 'past' && paymentAction ? 'Arrival and check-in are available on today’s board. Eligible payments can still be collected for these visits.' : scope === 'future' && compactHome ? (role === 'receptionist' ? 'Open a patient to reschedule or cancel. Arrival and check-in are available on today’s board.' : 'Confirm a booking, or open a patient to reschedule or cancel. Arrival and check-in are available on today’s board.') : DAY_BOARD_ACTIONS_TODAY_ONLY_COPY}
            </p>
          ) : null}
        </div>
      ) : (
        <>
          {/* Embedded chrome: the host owns the heading and the ROLE view-only
              line (WalkinApp); the row-cap honesty line still renders (never
              silently undercount), and the DAY action-denial line renders HERE
              so a non-today embedded board is never silently action-less. */}
          {!loading && !error && rows.length >= DAY_READ_ROW_CAP ? (
            <p className="day-board__viewonly" data-testid="day-board-truncated">
              {DAY_READ_TRUNCATED_COPY}
            </p>
          ) : null}
          {roleCanAct && !isToday && !(compactHome && role === 'receptionist') ? (
            <p
              className="day-board__viewonly"
              data-testid="day-board-actions-today-only"
            >
              {scope === 'past' && paymentAction ? 'Arrival and check-in are available on today’s board. Eligible payments can still be collected for these visits.' : scope === 'future' && compactHome ? (role === 'receptionist' ? 'Open a patient to reschedule or cancel. Arrival and check-in are available on today’s board.' : 'Confirm a booking, or open a patient to reschedule or cancel. Arrival and check-in are available on today’s board.') : DAY_BOARD_ACTIONS_TODAY_ONLY_COPY}
            </p>
          ) : null}
        </>
      )}

      {feedback ? (
        <p
          className={
            feedback.tone === 'success'
              ? 'day-board__feedback day-board__feedback--success'
              : 'day-board__feedback day-board__feedback--error'
          }
          role="status"
          tabIndex={-1}
          data-testid="day-board-feedback"
        >
          {feedback.text}
        </p>
      ) : null}

      {loading ? (
        // V7-P4 adoption: the shared LoadingState (03 P11), copy unchanged.
        <LoadingState label="Loading the clinic day..." lines={3} />
      ) : error ? (
        // V7-P4 adoption: the shared ErrorState (03 P11 S4), copy unchanged.
        <ErrorState title="The clinic day could not load right now. Refresh to retry." />
      ) : rows.length === 0 && !(compactHome && role === 'receptionist') ? (
        // DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1: the registered honest empty
        // state per scope, from THE ONE day-scope vocabulary. The 'today' arm
        // returns this board's existing copy byte-identically, so an
        // unadopted host prints exactly the same words as before.
        <EmptyState
          icon={<CalendarDays size={20} aria-hidden="true" />}
          title={emptyCopy.title}
          body={emptyCopy.body}
          testId="day-board-empty"
        />
      ) : (
        /* D5: the shared overflow-container idiom (03 P10) -- a genuinely wide
           table scrolls INSIDE its own container and the page never scrolls
           horizontally. Same primitive the DataTable uses; no new wrapper. */
        <div className="ac-table-scroll" data-testid="day-board-table-scroll">
        <table className="day-board__table">
          <thead>
            <tr>
              <th scope="col">Time</th>
              <th scope="col">Patient</th>
              <th scope="col">Doctor</th>
              {/* D1: Status + Ops status collapsed into ONE State column. */}
              <th scope="col">State</th>
              <th scope="col">{canAct || (roleCanAct && scope === 'future') || (scope === 'past' && paymentAction) ? 'Next' : ''}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr data-testid="day-board-empty"><td colSpan={5}>No visits for this day.</td></tr> : null}
            {rows.map((row) => {
              // D1: ONE State cell -- the stored-status chip plus the single
              // highest-precedence ops chip; the rest stay reachable as "+N".
              // DR-22: on the DOCTOR lens the allowance opens to ALL, so the
              // end states render inline and no "+N" is left to render.
              const state = dayStateCell(
                row,
                statusLabels,
                dayStateOpsInlineForRole(role),
              );
              // W-21 (CONSULT-INTAKE-RENDER-HEAL-V1, scope 3a): the DOCTOR
              // queue shows ONE current visit-lifecycle state per row -- never
              // the stored status stacked with the visit ops chip (the founder
              // walk saw "Checked in" + "In consultation" on one row). When the
              // row is in consultation or finalized, the visit ops chip IS the
              // latest canon state, so the stored-status chip stands down. The
              // desk lens is untouched (its deliberate D1 density design), and
              // money/conversion chips (a different axis) are never suppressed.
              // D13: Home leads with visit progress. Reuse the visit owner for
              // an active consult, even when money outranks it in other hosts.
              const homeStatus = compactHome ? currentVisitStatusChip(row, statusLabels) : state.status;
              const doctorVisitSupersedesStatus =
                role === 'doctor' &&
                (isInConsultation(row) || row.visitStatus === 'finalized');
              const busy = busyRowId === row.id;
              // PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-CHECKIN-SPINE-V1: the
              // row's intake-door verdict (pure). Flag off / non-action lens
              // = the neutral no-op -- the strip stays byte-identical.
              const intakePlan = intakeRowPlan(
                row,
                intakeSpineEnabled === true && canAct && onIntake !== undefined,
              );
              // ONE primary per row (adversarial finding F1): on a
              // collection-eligible row the Collect CTA is the row's single
              // primary (the money handoff IS the next act -- notably the
              // R-1 ungranted checked_in+finalized row), so the forward hop
              // demotes to a ghost. Same derivation the CTA slot uses. The
              // intake door, when primary, demotes the stored hop the same
              // way (one primary, never a wall).
              // STANDARD 4 (one grammar): the board's own ONE-PRIMARY demotion
              // speaks the shared workflow-handoff grammar. Identical boolean
              // (checkoutHandoff(row) !== null iff derivePaymentHandoff(row)
              // .kind === 'collect'), so every one-primary count pin is unchanged.
              const collectIsPrimary = checkoutHandoff(row) !== null;
              // D2: the row's ONE next action, from THE one pure machine (the
              // demotion rule above is now a named slot, not an inline ternary).
              const nextAction = dayBoardNextAction(row, {
                canAct,
                collectIsPrimary,
                intakeIsPrimary: intakePlan.intakeIsPrimary,
                suppressCheckedIn: intakePlan.suppressCheckedInAction,
              });
              const isNext = row.id === nextId;
              // T3: the searched-for row wears the found emphasis (a
              // presentation class beside --next; row logic untouched).
              const isFound =
                typeof highlightAppointmentId === 'number' &&
                row.id === highlightAppointmentId;
              const rowClassName =
                [
                  isNext ? 'day-board__row--next' : null,
                  isFound ? 'day-board__row--found' : null,
                ]
                  .filter((c): c is string => c !== null)
                  .join(' ') || undefined;
              return (
                <tr
                  key={row.id}
                  className={rowClassName}
                  data-testid={`day-board-row-${row.id}`}
                >
                  <td className="day-board__time" data-label="Time">
                    {formatIstTime(row.appointmentTime) || '--:--'}
                    {isNext && role !== 'receptionist' ? (
                      <AcBadge tone="brand" testId={`day-board-next-${row.id}`}>
                        Next
                      </AcBadge>
                    ) : null}
                    {/* ARRIVAL TRUTH (Scope 2a): the ACTUAL arrival moment --
                        the patient_arrived event's own timestamp -- as a
                        SECOND distinct fact under the scheduled slot time,
                        never a replacement. */}
                    {typeof row.arrivedAt === 'string' && row.arrivedAt !== '' ? (
                      <span
                        className="day-board__arrived-at"
                        data-testid={`day-board-arrived-${row.id}`}
                      >
                        Arrived {formatIstTime(row.arrivedAt) || '--:--'}
                      </span>
                    ) : null}
                    {/* D3: HOW LONG they have been waiting -- THE ONE
                        deterministic derivation (waitingMinutes), gated exactly
                        as Patient 360 gates it (arrived | checked_in only),
                        clock injected. No wire change: arrivedAt already ships. */}
                    {(() => {
                      // clockMs is nowMs on today and UNDEFINED off it: a
                      // stale day is never measured against the live clock.
                      if (typeof clockMs !== 'number' || doctorConsultOpen(row)) return null;
                      const waited = dayBoardWaitingMinutes(row, clockMs);
                      return waited === null ? null : (
                        <span
                          className="day-board__waiting"
                          data-testid={`day-board-waiting-${row.id}`}
                        >
                          {dayBoardWaitingLabel(waited)}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="day-board__patient" data-label="Patient">
                    {(recordDoorOpen && role === 'receptionist' && onOpenPatientRecord && (row.patientId || row.linkedPatientUmr)) || rowOpenKind(role, row) !== 'none' ? (
                      <button
                        type="button"
                        className="day-board__patient-link"
                        disabled={contextDisabled} onClick={() => {
                          if (recordDoorOpen && role === 'receptionist' && (row.patientId || row.linkedPatientUmr) && onOpenPatientRecord) onOpenPatientRecord(row);
                          else onOpenRow(row);
                        }}
                        data-testid={`day-board-open-${row.id}`}
                      >
                        {dayBoardPatientLabel(row)}
                      </button>
                    ) : (
                      // Role-shaped or absent: no drawer exists for this
                      // role/row, so no door renders (never a broken click).
                      // The class is truncation-safety only (the door branch
                      // above already had one) -- no copy, verdict or role
                      // behaviour changes with it.
                      <span
                        className="day-board__patient-name"
                        {...(row.leadId == null
                          ? {
                              title:
                                'No linked lead - opened records live on the patient surfaces',
                            }
                          : {})}
                      >
                        {dayBoardPatientLabel(row)}
                      </span>
                    )}
                    {/* FRONT-OFFICE-DAY-BOARD-TRUTH-AND-WAIT-CLOCK-V1: the
                        linked golden record's UMR, beside the identity it
                        belongs to. Server-resolved; an UNLINKED row renders
                        NOTHING here (never an invented or placeholder id).
                        Follows the #397 density-pass caption pattern -- the
                        same block caption the specialty and category lines
                        already use -- so a long name and a long UMR each get
                        their own line instead of colliding. */}
                    {(() => {
                      const umr = dayBoardUmr(row);
                      return umr === null ? null : (
                        <span
                          className="day-board__umr"
                          data-testid={`day-board-umr-${row.id}`}
                        >
                          {dayBoardUmrLabel(umr)}
                        </span>
                      );
                    })()}
                    {/* PDC-FO-GAP-CLOSURE-V1 (G-NEW): the new/returning
                        badge beside the identity it describes -- the ONE
                        owner derivation; an unlinked or unproven row
                        renders NOTHING (never a guessed badge). */}
                    {(() => {
                      const badge = dayBoardNewReturningBadge(row);
                      return badge === null ? null : (
                        <AcBadge
                          tone={badge.tone}
                          testId={`day-board-visit-badge-${row.id}`}
                        >
                          {badge.label}
                        </AcBadge>
                      );
                    })()}
                    {row.specialty ? (
                      <span className="day-board__specialty">{row.specialty}</span>
                    ) : null}
                    {!(compactHome && role === 'receptionist') && typeof row.bookingChannel === 'string' &&
                    row.bookingChannel !== '' ? (
                      /* Q-08=A: a quiet labeled FACT (S-5), never a chip --
                         the two-chip cap stays unspent. */
                      <span
                        className="day-board__specialty"
                        data-testid={`day-board-channel-${row.id}`}
                      >
                        {BOOKING_CHANNEL_FIELD_LABEL}: {bookingChannelLabel(row.bookingChannel)}
                      </span>
                    ) : null}
                    {!(compactHome && role === 'receptionist') && row.bookedBy?.trim() ? (
                      <span className="day-board__specialty" data-testid={`day-board-booked-by-${row.id}`}>
                        Booked by: {row.bookedBy}
                      </span>
                    ) : null}
                    {!(compactHome && role === 'receptionist') && row.bookingVisitType?.trim() ? (
                      <span className="day-board__specialty" data-testid={`day-board-visit-type-${row.id}`}>
                        Appointment type: {bookingChannelLabel(row.bookingVisitType)}
                      </span>
                    ) : null}
                    {row.registrationAlertFlag === true ? (
                      /* FD-34: the handle-with-care marker VISIBLE where the
                         desk works the day (boolean fact; the detail stays
                         deliberately absent -- a reason would be clinical). */
                      <span
                        className="day-board__alert"
                        data-testid={`day-board-alert-${row.id}`}
                      >
                        Handle with care
                      </span>
                    ) : null}
                    {typeof row.chiefComplaintCategory === 'string' &&
                    row.chiefComplaintCategory !== '' ? (
                      <span
                        className="day-board__category"
                        data-testid={`day-board-category-${row.id}`}
                      >
                        {row.chiefComplaintCategory}
                      </span>
                    ) : null}
                    {/* WALK-IN-COMPLETION-V1 (C-3 IDENTITY HONESTY), reworded
                        by AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F7/W-11): a
                        lead row with NO golden-record link stays VISIBLY
                        identity-pending on the queue - never silently
                        treated as linked - but the chip speaks PLAIN WORDS
                        derived from registration truth, never a machine
                        state token. */}
                    {(() => {
                      const identityChip = dayBoardIdentityChip(row, role);
                      return identityChip !== null ? (
                        <AcBadge
                          tone="neutral"
                          testId={`day-board-link-pending-${row.id}`}
                        >
                          {identityChip}
                        </AcBadge>
                      ) : null;
                    })()}
                  </td>
                  <td
                    className="day-board__doctor"
                    data-label="Doctor"
                    data-testid={`day-board-doctor-${row.id}`}
                  >
                    {/* SEP15: assigned-doctor truth (roster name or the
                        explicit Unassigned) + the queue-readiness line from
                        THE ONE evaluator (deriveDoctorQueueState) -- rendered
                        only in its two queue-speaking states so no fact
                        renders twice. */}
                    {canAct && onAssignDoctor !== undefined && clinicDoctors !== undefined &&
                    canAssignDoctorOnRow(row.status) && !(role === 'receptionist' ? receptionDoctorAssignmentLocked(row) : doctorConsultOpen(row)) ? (
                      <button type="button" className="day-board__doctor-link" disabled={busy || contextDisabled}
                        onClick={() => onAssignDoctor(row)} data-testid={`day-board-assign-${row.id}`}
                        aria-label={`${row.assignedDoctorId != null ? 'Change' : 'Assign'} doctor for ${dayBoardPatientLabel(row)}`}>
                        {assignedDoctorLabel(row)}
                      </button>
                    ) : <span className="day-board__doctor-name">{assignedDoctorLabel(row)}</span>}
                    {(() => {
                      // Reception still owes check-in after registration saves.
                      // Keep the shared Doctor startability evaluator unchanged.
                      const readiness = role === 'receptionist' && row.status !== 'checked_in'
                        ? null
                        : dayBoardQueueReadiness(row);
                      return readiness !== null && !compactHome ? (
                        <AcBadge
                          tone={
                            readiness.state === 'ready_for_doctor'
                              ? 'positive'
                              : 'pending'
                          }
                          testId={`day-board-readiness-${row.id}`}
                        >
                          {readiness.label}
                        </AcBadge>
                      ) : null;
                    })()}
                  </td>
                  {/* D1: THE ONE State cell -- the stored status chip (R-C:
                      vocabulary frozen, dayStatusChip byte-identical) plus the
                      single highest-precedence ops chip; the remainder renders
                      "+N" and stays one click deeper in the row's EXISTING
                      drawer (EVIDENCE-ROWS law -- no fact deleted). */}
                  <td
                    className="day-board__state"
                    data-label="State"
                    data-testid={`day-board-ops-${row.id}`}
                  >
                    {/* W-21 (scope 3a): on the doctor lens the stored-status
                        chip stands down when a visit ops chip supersedes it, so
                        the row shows ONE current state, never a stack. Every
                        other lens renders the stored-status chip byte-identical. */}
                    {compactHome || !doctorVisitSupersedesStatus ? (
                      <AcBadge
                        tone={homeStatus.tone}
                        testId={`day-board-status-${row.id}`}
                      >
                        {homeStatus.label}
                      </AcBadge>
                    ) : null}
                    {/* FRONT-OFFICE-DAY-BOARD-TRUTH-AND-WAIT-CLOCK-V1: the
                        CURRENT-STAGE wait clock -- TIME ONLY, adjacent to the
                        stored-status chip that already names the stage. THE
                        ONE chip primitive, always the NEUTRAL tone: no
                        threshold colors, no warning states (escalation
                        thresholds are a future config-backed package; the
                        seven-color law is untouched). Rendered for ARRIVED-OR-
                        LATER stages only (founder ruling R3): absent on every
                        pre-arrival row, absent on terminal rows, absent when no
                        entry moment was recorded, and absent on `arrived` where
                        the Time cell's waiting line IS the arrived clock
                        (founder ruling R1). Not a State chip: dayStateCell and
                        its two-chip cap are untouched. */}
                    {(() => {
                      if (typeof clockMs !== 'number' || doctorConsultOpen(row)) return null;
                      // S12, frozen 8 October: one arrival-based waiting duration on compact Reception.
                      if (compactHome && role === 'receptionist' && dayBoardWaitingMinutes(row, clockMs) !== null) return null;
                      const elapsed = dayBoardStageElapsedMinutes(row, clockMs);
                      return elapsed === null ? null : (
                        <AcBadge
                          tone="neutral"
                          title={DAY_STAGE_CLOCK_TITLE}
                          testId={`day-board-stage-clock-${row.id}`}
                        >
                          {dayBoardStageElapsedLabel(elapsed)}
                        </AcBadge>
                      );
                    })()}
                    {state.primary !== null && !compactHome ? (
                      state.primary.canon !== undefined ? (
                        // V7-P4 adoption: THE StatusChip canon renders the
                        // chip (canon tone == this board's proven tone,
                        // test-pinned); the label stays this screen's
                        // pinned copy, passed caller-resolved.
                        <StatusChip
                          domain={state.primary.canon.domain}
                          status={state.primary.canon.status}
                          label={state.primary.label}
                        />
                      ) : (
                        <AcBadge tone={state.primary.tone}>
                          {state.primary.label}
                        </AcBadge>
                      )
                    ) : null}
                    {/* DR-22 INLINE OPS CHIPS: empty under the default cap
                        (every desk lens byte-identical); on the doctor lens
                        these are the end states that used to hide behind
                        "+N". Same chip grammar as `primary` -- the StatusChip
                        canon when the chip carries one, the board's proven
                        tone otherwise. */}
                    {(!compactHome ? state.inline : []).map((chip) =>
                      chip.canon !== undefined ? (
                        <StatusChip
                          key={chip.label}
                          domain={chip.canon.domain}
                          status={chip.canon.status}
                          label={chip.label}
                        />
                      ) : (
                        <AcBadge key={chip.label} tone={chip.tone}>
                          {chip.label}
                        </AcBadge>
                      ),
                    )}
                    {!compactHome && state.overflow.length > 0 ? (
                      rowOpenKind(role, row) !== 'none' ? (
                        <button
                          type="button"
                          className="day-board__state-more"
                          title={`${DAY_STATE_OVERFLOW_TITLE}: ${state.overflow
                            .map((o) => o.label)
                            .join('; ')}`}
                          disabled={contextDisabled} onClick={() => onOpenRow(row)}
                          data-testid={`day-board-state-more-${row.id}`}
                        >
                          {dayStateOverflowLabel(state.overflow.length)}
                        </button>
                      ) : (
                        (() => {
                          // FRONT-DESK-DAY-BOARD-V1 (WALK-IN APPOINTMENTS
                          // row): a drawer-less LINKED row's +N opens the
                          // EXISTING patient record (the same door + gate as
                          // the continuity link); only a row with no backing
                          // surface anywhere keeps the honest inert span
                          // (facts still ride the title, never deleted).
                          const overflowHref = dayStateOverflowHref(
                            role,
                            row,
                            recordDoorOpen,
                          );
                          const overflowTitle = `${DAY_STATE_OVERFLOW_TITLE}: ${state.overflow
                            .map((o) => o.label)
                            .join('; ')}`;
                          return overflowHref !== null ? (
                            <a
                              className="day-board__state-more"
                              href={overflowHref}
                              title={overflowTitle}
                              data-testid={`day-board-state-more-${row.id}`}
                            >
                              {dayStateOverflowLabel(state.overflow.length)}
                            </a>
                          ) : (
                            // Role-shaped or absent (LES-016): no surface
                            // exists for this role/row, so no DOOR renders --
                            // and FRONT-DESK-CORRECTIVE-V1 (L-04) supplies
                            // what a door would have reached. The facts are
                            // DISCLOSED IN PLACE, never a dead affordance.
                            dayStateOverflowDisclosure(
                              state.overflow,
                              row.id,
                              dayStateOverflowLabel(state.overflow.length),
                            )
                          );
                        })()
                      )
                    ) : null}
                    {/* FRONT-OFFICE-DAY-BOARD-TRUTH-AND-WAIT-CLOCK-V1: the
                        episode's OUTSTANDING amount, in the row context the
                        obligation belongs to and beside the payment fact the
                        State cell already ranks first. A LABELED MONEY FIELD,
                        not a chip -- typed minor units through THE ONE
                        formatter -- so the D1 two-chip cap is untouched and
                        the number never reads as a fifth state. Server-
                        derived by THE ONE netting derivation; a settled,
                        unpriced or not-yet-closed episode renders NOTHING (no
                        noisy zero), and a non-money-audience role never
                        receives the field at all. DISPLAY ONLY: no collect
                        affordance is added here -- the row's existing payment
                        door is the only money control. */}
                    {(() => {
                      const due = compactHome && role === 'receptionist' ? null : dayBoardDueMinorUnits(row);
                      return due === null ? null : (
                        <span
                          className="day-board__due"
                          data-testid={`day-board-due-${row.id}`}
                        >
                          {dayBoardDueLabel(due)}
                        </span>
                      );
                    })()}
                    {/* RECEPTION-MONEY-TRUTH-V1 (A2): the repeat-visit
                        CARRIED BALANCE -- the SAME labeled-money-field
                        idiom as Due above (additive inside the approved
                        pattern; no new chip, no layout change). The
                        founder-reported break this closes: the desk could
                        not tell a returning patient owed money from before
                        today. Money audience only by wire construction. */}
                    {(() => {
                      const carried = compactHome && role === 'receptionist' ? null : dayBoardCarriedMinorUnits(row);
                      return carried === null ? null : (
                        <span
                          className="day-board__due day-board__due--carried"
                          data-testid={`day-board-carried-${row.id}`}
                        >
                          {dayBoardCarriedLabel(carried)}
                        </span>
                      );
                    })()}
                    {/* A2: the "payment happened before" row fact -- the
                        desk can tell a returning patient has paid earlier
                        without leaving the board. Same labeled-money-field
                        idiom; renders ONLY when prior active money exists. */}
                    {(() => {
                      const priorPaid = compactHome && role === 'receptionist' ? null : dayBoardPriorPaidMinorUnits(row);
                      return priorPaid === null ? null : (
                        <span
                          className="day-board__due day-board__due--prior-paid"
                          data-testid={`day-board-prior-paid-${row.id}`}
                        >
                          {dayBoardPriorPaidLabel(priorPaid)}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="day-board__actions" data-label="Next">
                    {intakePlan.intakeAction !== null && onIntake ? (
                      <AcButton
                        size="sm"
                        variant={
                          intakePlan.intakeIsPrimary && !collectIsPrimary
                            ? 'primary'
                            : 'ghost'
                        }
                        disabled={busy || contextDisabled}
                        onClick={() => onIntake(row)}
                        data-testid={`day-board-action-intake-${row.id}`}
                      >
                        {intakePlan.intakeActionLabel}
                      </AcButton>
                    ) : null}
                    {/* D2: ONE primary, at most ONE visible secondary, the rest
                        behind the overflow -- ACTION LENS ONLY (a view-only lens
                        gets the empty verdict, so denial stays ABSENCE, never a
                        disabled control). */}
                    {nextAction.primary !== null ? (
                      <AcButton
                        size="sm"
                        variant="primary"
                        disabled={busy || contextDisabled}
                        onClick={() => onAction(row, nextAction.primary!)}
                        data-testid={`day-board-action-${nextAction.primary}-${row.id}`}
                      >
                        {dayBoardActionLabel(nextAction.primary, compactHome && role === 'receptionist')}
                      </AcButton>
                    ) : null}
                    {nextAction.secondary !== null && !compactHome ? (
                      <AcButton
                        size="sm"
                        variant="ghost"
                        disabled={busy || contextDisabled}
                        onClick={() => onAction(row, nextAction.secondary!)}
                        data-testid={`day-board-action-${nextAction.secondary}-${row.id}`}
                      >
                        {dayBoardActionLabel(nextAction.secondary, compactHome && role === 'receptionist')}
                      </AcButton>
                    ) : null}
                    {nextAction.overflow.length > 0 && !compactHome ? (
                      <details
                        className="day-board__more"
                        data-testid={`day-board-more-${row.id}`}
                      >
                        <summary className="day-board__more-summary">
                          {dayActionOverflowLabel(nextAction.overflow.length)}
                        </summary>
                        <div className="day-board__more-list">
                          {nextAction.overflow.map((a) => (
                            <AcButton
                              key={a}
                              size="sm"
                              variant="ghost"
                              disabled={busy || contextDisabled}
                              onClick={() => onAction(row, a)}
                              data-testid={`day-board-action-${a}-${row.id}`}
                            >
                              {dayBoardActionLabel(a, compactHome && role === 'receptionist')}
                            </AcButton>
                          ))}
                        </div>
                      </details>
                    ) : null}
                    {/* Collection belongs to the current financial day even when
                        the selected clinical visit is in the past. The existing
                        payment writer enforces cash-day/role/clinic gates.
                        Clinical and contact actions remain today-only. */}
                    {scope !== 'future' && paymentAction ? paymentAction(row) : null}
                    {isToday && caseSheetAction ? caseSheetAction(row) : null}
                    {isToday && contactRevealAction ? contactRevealAction(row) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// --- Wired container ---------------------------------------------------------

/** Injected mutation set (tests pass mocks; the default wires the client).
 *  cancel gained an OPTIONAL reasonCode (rider R-08) -- the reason picker's
 *  controlled token, forwarded to the cancel endpoint body. */
export interface DayBoardMutations {
  readonly confirm: (id: number) => Promise<unknown>;
  readonly arrived: (id: number) => Promise<unknown>;
  readonly checkedIn: (id: number, identityChecked?: boolean, returningCheckIn?: boolean) => Promise<unknown>;
  readonly fulfilled: (id: number) => Promise<unknown>;
  readonly cancel: (id: number, reasonCode?: string, expectedAppointmentTime?: string) => Promise<unknown>;
  readonly noShow: (id: number) => Promise<unknown>;
  /** DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1 (S1): the manual
   *  assign/reassign writer (roster id, never free text). */
  readonly assignDoctor: (id: number, doctorId: number, reasonCode?: string) => Promise<unknown>;
}

export const DEFAULT_DAY_BOARD_MUTATIONS: DayBoardMutations = {
  confirm: confirmAppointment,
  arrived: markAppointmentArrived,
  checkedIn: markAppointmentCheckedIn,
  fulfilled: markAppointmentFulfilled,
  cancel: cancelAppointment,
  noShow: markAppointmentNoShow,
  assignDoctor: assignAppointmentDoctor,
};

export interface ClinicDayBoardProps {
  /** The host’s already-scoped single-clinic read context; never a permission grant. */
  readonly bookingClinic?: { readonly id: number; readonly label: string | null } | null;
  /** A quick-view request for this exact board row. The board rechecks its
   *  current row, day and role before using its normal guarded action path. */
  readonly requestedAction?: { readonly appointmentId: number; readonly action: DayBoardActionKind | 'intake' | 'assign_doctor'; readonly requestId: number } | null;
  readonly onRequestedActionHandled?: () => void;
  readonly onOpenLead?: (leadId: number, title: string) => void;
  readonly onOpenPatientRecord?: (row: TodayAppointment) => void;
  readonly contextDisabled?: boolean;
  readonly rows: readonly TodayAppointment[];
  readonly role: string | null;
  readonly loading: boolean;
  readonly error: boolean;
  /** Refetch the day read after a successful mutation. */
  readonly onChanged: () => void;
  readonly mutations?: DayBoardMutations;
  readonly paymentAction?: (row: TodayAppointment) => ReactNode;
  readonly caseSheetAction?: (row: TodayAppointment) => ReactNode;
  readonly contactRevealAction?: (row: TodayAppointment) => ReactNode;
  /** See ClinicDayBoardViewProps.chrome (default 'full'). */
  readonly chrome?: 'full' | 'embedded';
  /** Home-only density: one status, remaining recorded facts in Details. */
  readonly compactHome?: boolean;
  /** FRONTDESK-CHECKIN-AND-IDENTITY-V1 (Scope 1): the check-in identity
   *  step's UX gate -- the host passes { bridgeEnabled: canUseLeadBridge(me) }.
   *  When ABSENT the step still opens for the receptionist on an unlinked
   *  lead row but renders the honest gate-dark line (fail-closed; the skip
   *  door always works -- identity never blocks the queue). */
  readonly checkinIdentity?: { readonly bridgeEnabled: boolean };
  /** The org's cancel-reason vocabulary for the reason picker (rider R-08;
   *  host passes resolveCancelReasonCodes(me)). Defaults to the seeded
   *  code-default list. */
  readonly cancelReasonCodes?: readonly string[];
  /** PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-CHECKIN-SPINE-V1: the intake-
   *  spine lens -- the host passes { enabled: me.intakeSpineEnabled === true }.
   *  Absent/false = the V3-P6 workflow byte-identical (the rollback path):
   *  identity-step routing, no intake doors, no auto check-in. */
  readonly intakeSpine?: { readonly enabled: boolean };
  /** SEP15 vertical: the Patient 360 continuity door's UX gate -- the host
   *  passes { enabled: me.patientMasterEnabled === true }. Absent/false =
   *  no door renders (byte-identical board). */
  readonly patientRecordDoor?: { readonly enabled: boolean };
  /** DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: the clinic's ACTIVE roster
   *  (data.clinicDoctors off the board read). Absent = no assign
   *  affordance (byte-identical board for hosts that do not pass it). */
  readonly clinicDoctors?: readonly ClinicRosterDoctor[];
  /** FRONT-OFFICE-DENSITY-PASS-V1 (D3): the host-injected clock for the
   *  waiting-time line. Absent = no waiting line (byte-identical board). */
  readonly nowMs?: number;
  /** RECEPTION-OS-BUILD-V1 (F-09/F-12): org display-label overrides for
   *  appointment statuses (host passes me.appointmentStatusLabels). Absent =
   *  the StatusChip canon defaults. */
  readonly statusLabels?: AppointmentStatusLabelOverrides | null;
  /** DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1: WHICH day these rows belong to --
   *  the host passes the day-scope hook's verdict. Absent / 'today' =
   *  byte-identical board. See ClinicDayBoardViewProps.dayScope for the law
   *  it enforces (read-side only; no writer, route or fence changes). */
  readonly dayScope?: DayScopeKind;
  /** T3 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1): see
   *  ClinicDayBoardViewProps.highlightAppointmentId (presentation only). */
  readonly highlightAppointmentId?: number | null;
  /** A dock opened from a visible row must preserve the queue scroll. */
  readonly scrollHighlightIntoView?: boolean;
}

/** Name-click routing for Reception's existing registration/appointment doors. */
export function receptionRowOpenTarget(row: TodayAppointment, dayScope: DayScopeKind, intakeEnabled: boolean): 'registration' | 'appointment' {
  return dayScope === 'today' && (intakeRowPlan(row, intakeEnabled).intakeAction !== null ||
    (intakeEnabled && row.hasPatientRecord !== true && row.intakeStatus === 'in_progress' && row.intakeMode !== 'paper' && (row.status === 'arrived' || row.status === 'checked_in')))
    ? 'registration' : 'appointment';
}

export default function ClinicDayBoard({
  bookingClinic,
  requestedAction,
  onRequestedActionHandled,
  onOpenLead,
  onOpenPatientRecord,
  contextDisabled = false,
  rows,
  role,
  loading,
  error,
  onChanged,
  mutations = DEFAULT_DAY_BOARD_MUTATIONS,
  paymentAction,
  caseSheetAction,
  contactRevealAction,
  chrome,
  compactHome = false,
  checkinIdentity,
  cancelReasonCodes = DEFAULT_CANCEL_REASON_CODES,
  intakeSpine,
  patientRecordDoor,
  clinicDoctors,
  nowMs,
  statusLabels,
  dayScope,
  highlightAppointmentId,
  scrollHighlightIntoView = true,
}: ClinicDayBoardProps): JSX.Element {
  const intakeSpineEnabled = intakeSpine?.enabled === true;
  const [feedback, setFeedback] = useState<DayBoardFeedback | null>(null);
  const [busyRowId, setBusyRowId] = useState<number | null>(null);
  const [openLead, setOpenLead] = useState<{
    leadId: number;
    title: string;
  } | null>(null);
  const [openDoctorRow, setOpenDoctorRow] = useState<{
    appointmentId: number;
    title: string;
  } | null>(null);
  // FRONTDESK-CHECKIN-AND-IDENTITY-V1: the held check-in hop while the
  // identity step is open (Scope 1a), and the cancel row while the reason
  // picker is open (rider R-08; replaces the cancel window.confirm -- the
  // no-show confirm is untouched).
  const [identityStep, setIdentityStep] = useState<{
    row: TodayAppointment;
    action: CheckinIdentityAction;
  } | null>(null);
  const [visitIdentityChecked, setVisitIdentityChecked] = useState(false);
  const [visitConfirmation, setVisitConfirmation] = useState<{
    row: TodayAppointment; action: 'arrived' | 'checked_in'; returning?: boolean;
  } | null>(null);
  const visitSaving = useRef(false);
  const visitReturnFocus = useRef<HTMLElement | null>(null);
  const visitReturnId = useRef<number | null>(null);
  useEffect(() => {
    if (visitConfirmation || contextDisabled || visitReturnId.current === null) return;
    const id = visitReturnId.current;
    visitReturnId.current = null;
    requestAnimationFrame(() => {
      const prior = visitReturnFocus.current;
      if (prior?.isConnected && !prior.hasAttribute('disabled')) prior.focus();
      else document.querySelector<HTMLElement>(`[data-testid="day-board-open-${id}"]`)?.focus();
    });
  }, [visitConfirmation, contextDisabled]);
  const [cancelRow, setCancelRow] = useState<TodayAppointment | null>(null);
  // HS04: opening the focused Reception Home action drawer is read-only.
  const [fulfillRow, setFulfillRow] = useState<TodayAppointment | null>(null);
  const fulfillingRef = useRef(false);
  const fulfillReturnFocusRef = useRef<HTMLElement | null>(null);
  const fulfillWasOpenRef = useRef(false);

  useEffect(() => {
    if (fulfillRow !== null) {
      fulfillWasOpenRef.current = true;
      return;
    }
    if (!fulfillWasOpenRef.current) return;
    fulfillWasOpenRef.current = false;
    const original = fulfillReturnFocusRef.current;
    const target = original?.isConnected
      ? original
      : document.querySelector<HTMLElement>('[data-testid="day-board-feedback"]');
    target?.focus();
  }, [fulfillRow]);
  // PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-CHECKIN-SPINE-V1: the intake
  // drawer's row while open (flag-on lens only).
  const [intakeRow, setIntakeRow] = useState<TodayAppointment | null>(null);
  // AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3): the Complete Registration
  // modal host (fresh starts; sessions in flight keep their surfaces).
  const [registrationRow, setRegistrationRow] = useState<TodayAppointment | null>(null);
  // WALK-3-REGISTRATION-CONTAINER-FIX-V1 (C4): TRUE when the modal opens
  // straight to the form (the RESUME path for a digital session already in
  // progress) instead of the one-question choice -- the resume no longer
  // navigates to the patient-mode page in a new tab.
  const [registrationStartInForm, setRegistrationStartInForm] = useState(false);
  // DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: the row whose assign picker is
  // open (action lens only).
  const [assignRow, setAssignRow] = useState<TodayAppointment | null>(null);
  const [assignmentStopped, setAssignmentStopped] = useState<ReadonlySet<number>>(new Set());
  const [noShowRow, setNoShowRow] = useState<TodayAppointment | null>(null);
  const assignReturnFocus = useRef<HTMLElement | null>(null);
  const assignReturnId = useRef<number | null>(null);
  const assignReturnExpectedDoctor = useRef<number | null>(null);
  useEffect(() => {
    if (assignRow !== null || assignReturnId.current === null) return;
    const id = assignReturnId.current;
    if (assignReturnExpectedDoctor.current !== null &&
        rows.find(row => row.id === id)?.assignedDoctorId !== assignReturnExpectedDoctor.current) return;
    assignReturnId.current = null;
    assignReturnExpectedDoctor.current = null;
    const original = assignReturnFocus.current;
    const target = original?.isConnected && !original.hasAttribute('disabled')
      ? original
      : document.querySelector<HTMLElement>(`[data-testid="day-board-assign-${id}"]`);
    target?.focus();
  }, [assignRow, rows]);

  /** Fire the existing mutation path for one hop (unchanged semantics). */
  const fireAction = (
    row: TodayAppointment,
    action: DayBoardActionKind,
    reasonCode?: string,
    identityChecked = false,
    returningCheckIn = false,
  ): void => {
    if (action === 'arrived' || action === 'checked_in') {
      if (visitSaving.current) return;
      visitSaving.current = true;
    }
    if (action === 'fulfilled') {
      if (fulfillingRef.current) return;
      fulfillingRef.current = true;
    }
    setBusyRowId(row.id);
    setFeedback(null);
    const run =
      action === 'confirm'
        ? mutations.confirm(row.id)
        : action === 'arrived'
          ? mutations.arrived(row.id)
          : action === 'checked_in'
            ? (returningCheckIn ? mutations.checkedIn(row.id, identityChecked, true) : mutations.checkedIn(row.id, identityChecked))
            : action === 'fulfilled'
              ? mutations.fulfilled(row.id)
              : action === 'cancel'
                ? mutations.cancel(row.id, reasonCode, row.appointmentTime)
                : mutations.noShow(row.id);
    run
      .then(() => {
        // HANDOFF LINE (V7-P4, #285): the check-in completion moment names
        // WHO acts next (the doctor, from their Today) -- the next step
        // belongs to another role, so it is a status line, never a door.
        setFeedback({
          tone: 'success',
          text:
            action === 'checked_in'
              ? checkedInSuccessFeedback(ACTION_SUCCESS[action])
              : ACTION_SUCCESS[action],
        });
        setCancelRow(null);
        if (action === 'arrived' || action === 'checked_in') setVisitConfirmation(null);
        if (action === 'no_show') setNoShowRow(null);
        if (action === 'fulfilled') setFulfillRow(null);
        onChanged();
      })
      .catch((err: unknown) => {
        setFeedback({
          tone: 'error',
          text: appointmentErrorMessage(action, err),
        });
        // No earlier visit on record after all: the unchanged registration window takes over.
        if (returningCheckIn && err instanceof PortalApiError && err.code === 'returning_patient_not_established') {
          setVisitConfirmation(null);
          setRegistrationStartInForm(false);
          setRegistrationRow(row);
        }
        // Keep the selected reason and exact appointment for retry/readback.
        if (action === 'no_show') setNoShowRow(null);
        // Adversarial-review F4: a rejection usually means the row is STALE
        // (another operator moved it) -- refetch so the strip re-derives from
        // the server truth instead of inviting a repeat press.
        // Cancellation retains its exact attempt until retry/readback resolves it.
        // Refreshing can move the row into Finished and unmount this dialog.
        if (action !== 'cancel') onChanged();
      })
      .finally(() => {
        if (action === 'arrived' || action === 'checked_in') visitSaving.current = false;
        if (action === 'fulfilled') fulfillingRef.current = false;
        setBusyRowId(null);
      });
  };

  // A lost response may follow a committed write. The refetched row, not a
  // second press or a guessed failure message, resolves that uncertainty.
  useEffect(() => {
    if (fulfillRow === null) return;
    if (role !== 'receptionist' || (dayScope ?? 'today') !== 'today') {
      setFulfillRow(null);
      return;
    }
    const current = rows.find((row) => row.id === fulfillRow.id);
    if (current?.status === 'fulfilled') {
      setFulfillRow(null);
      setFeedback({ tone: 'success', text: FULFILLED_SUCCESS_COPY });
    } else if (current !== undefined && !sameFulfillmentTarget(fulfillRow, current)) {
      setFulfillRow(null);
      setFeedback({ tone: 'error', text: 'This appointment changed. Review the day before acting.' });
    }
  }, [rows, fulfillRow, role, dayScope]);

  useEffect(() => {
    if (!visitConfirmation || loading || error) return;
    const { row: held, action } = visitConfirmation;
    const current = rows.find(row => row.id === held.id);
    if (!current || role !== 'receptionist' || (dayScope ?? 'today') !== 'today' ||
        current.patientId !== held.patientId || current.clinicId !== held.clinicId ||
        current.appointmentTime !== held.appointmentTime || current.assignedDoctorId !== held.assignedDoctorId) {
      setVisitConfirmation(null);
      setFeedback({ tone: 'error', text: 'This appointment changed. Review the day before acting.' });
    } else if (current.status !== held.status && !visitSaving.current) {
      setVisitConfirmation(null);
      const saved = current.status === action || (action === 'arrived' && current.status === 'checked_in');
      setFeedback(saved
        ? { tone: 'success', text: ACTION_SUCCESS[current.status === 'checked_in' ? 'checked_in' : 'arrived'] }
        : { tone: 'error', text: 'This appointment changed. Review the day before acting.' });
    }
  }, [rows, visitConfirmation, role, dayScope, busyRowId, loading, error]);

  /** DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: fire the S1 assign writer
   *  (same success/error/refetch posture as fireAction -- a 409 message
   *  passes through VERBATIM and the board refetches either way, so a
   *  stale row re-derives from server truth). */
  const fireAssignDoctor = (row: TodayAppointment, doctorId: number, reasonCode?: string): void => {
    if (assignmentStopped.has(row.id)) return;
    const current = rows.find(candidate => candidate.id === row.id);
    if (!current || (role === 'receptionist' ? receptionDoctorAssignmentLocked(current) : doctorConsultOpen(current)) || !canAssignDoctorOnRow(current.status)) {
      setAssignRow(null);
      setFeedback({ tone: 'error', text: 'Consultation has started or the appointment changed. The doctor cannot be changed here.' });
      onChanged();
      return;
    }
    setBusyRowId(row.id);
    setFeedback(null);
    mutations
      .assignDoctor(row.id, doctorId, reasonCode)
      .then(() => {
        setFeedback({ tone: 'success', text: ASSIGN_DOCTOR_SUCCESS_COPY });
        assignReturnExpectedDoctor.current = doctorId;
        setAssignRow(null);
        onChanged();
      })
      .catch((err: unknown) => {
        assignReturnExpectedDoctor.current = null;
        if (err instanceof PortalApiError && err.code === 'consultation_started') {
          setAssignmentStopped(previous => new Set([...previous, row.id]));
        }
        setFeedback({
          tone: 'error',
          text: appointmentErrorMessage('assign_doctor', err),
        });
        onChanged();
      })
      .finally(() => setBusyRowId(null));
  };

  const runAction = (row: TodayAppointment, action: DayBoardActionKind): void => {
    if (role === 'receptionist' && action === 'fulfilled') return;
    if (role === 'receptionist' && action === 'confirm') return;
    if (role === 'receptionist' && action === 'checked_in') {
      if (contextDisabled || (dayScope ?? 'today') !== 'today' || busyRowId !== null) return;
      setFeedback(null);
      // A known returning patient checks in without repeating registration
      // (Ravi, 16 Oct 2026); new and unfinished registrations keep the window.
      if (isReturningCheckInRow(row)) {
        visitReturnFocus.current = document.activeElement as HTMLElement | null;
        visitReturnId.current = row.id;
        setVisitConfirmation({ row, action, returning: true });
        return;
      }
      setRegistrationStartInForm(row.intakeStatus === 'in_progress' && row.intakeMode !== 'paper');
      setRegistrationRow(row);
      return;
    }

    // Physical arrival uses the existing explicit arrival confirmation for
    // linked and unregistered bookings alike. Registration follows through
    // CompleteRegistrationModal; never enter the legacy conversion form here.
    if (role === 'receptionist' && action === 'arrived') {
      if (contextDisabled || (dayScope ?? 'today') !== 'today' || busyRowId !== null) return;
      visitReturnFocus.current = document.activeElement as HTMLElement | null;
      visitReturnId.current = row.id;
      setFeedback(null);
      setVisitIdentityChecked(false);
      setVisitConfirmation({ row, action });
      return;
    }
    // Route the act (pure verdict; only the action role sees action buttons,
    // so the identity step fires for the receptionist lens exactly).
    const route = dayBoardActionRoute(row, action, {
      intakeSpineEnabled,
      fulfillmentConfirmationEnabled: compactHome && role === 'receptionist',
    });
    if (route === 'identity_step') {
      // THE CHECK-IN IDENTITY STEP (Scope 1a): register-or-link FIRST -- one
      // step from the desk; the skip door inside preserves the queue.
      setFeedback(null);
      setIdentityStep({ row, action: action as CheckinIdentityAction });
      return;
    }
    if (route === 'cancel_dialog') {
      // Cancel: the reason picker (rider R-08) replaces the window.confirm --
      // same destructive gravity, one honest reason recorded.
      setFeedback(null);
      setCancelRow(row);
      return;
    }
    if (route === 'fulfill_dialog') {
      if (contextDisabled || (dayScope ?? 'today') !== 'today' || busyRowId !== null) return;
      fulfillReturnFocusRef.current = document.activeElement as HTMLElement | null;
      setFeedback(null);
      setFulfillRow(row);
      return;
    }
    if (route === 'no_show_dialog') {
      if (contextDisabled || (dayScope ?? 'today') !== 'today' || busyRowId !== null) return;
      setFeedback(null);
      setNoShowRow(row);
      return;
    }
    fireAction(row, action);
  };

  const openRowIntake = (row: TodayAppointment): void => {
          if (contextDisabled || busyRowId !== null || (dayScope ?? 'today') !== 'today') return;
          setFeedback(null);
          // Online and already-linked patients use the same approved large
          // appointment-bound registration form and completion writer.
          if (row.status === 'arrived' && role === 'receptionist') {
            setRegistrationStartInForm(row.intakeStatus === 'in_progress' && row.intakeMode !== 'paper');
            setRegistrationRow(row);
            return;
          }
          // AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3) + WALK-3 (C4): the door's
          // THREE shapes -- a fresh start opens the Complete Registration MODAL
          // (one question, two options); a digital in-progress session RESUMES
          // the ONE form INSIDE the same modal (no re-ask, no navigation, no
          // new tab -- founder walk W-13); paper / completed sessions keep the
          // legacy review drawer (closeable).
          if (row.intakeStatus === 'in_progress' && row.intakeMode !== 'paper') {
            setRegistrationStartInForm(true);
            setRegistrationRow(row);
            return;
          }
          if (row.intakeStatus === 'in_progress' || row.intakeStatus === 'completed') {
            setIntakeRow(row);
            return;
          }
          setRegistrationStartInForm(false);
          setRegistrationRow(row);
  };

  const lastHandledRequest = useRef<number | null>(null);
  useEffect(() => {
    if (requestedAction === null || requestedAction === undefined ||
        lastHandledRequest.current === requestedAction.requestId || contextDisabled) return;
    const row = rows.find(candidate => candidate.id === requestedAction.appointmentId);
    if (row === undefined) return;
    lastHandledRequest.current = requestedAction.requestId;
    onRequestedActionHandled?.();
    if (requestedAction.action === 'intake') {
      if (role === 'receptionist' && (dayScope ?? 'today') === 'today' && busyRowId === null &&
          intakeRowPlan(row, intakeSpineEnabled).intakeAction !== null) openRowIntake(row);
      return;
    }
    if (requestedAction.action === 'assign_doctor') {
      if (role === 'receptionist' && (dayScope ?? 'today') === 'today' && busyRowId === null &&
          canAssignDoctorOnRow(row.status) && !receptionDoctorAssignmentLocked(row)) {
        setFeedback(null);
        // The quick-view trigger has unmounted; retain the same appointment's board door.
        assignReturnFocus.current = document.querySelector<HTMLElement>(`[data-testid="day-board-assign-${row.id}"]`);
        assignReturnFocus.current?.focus();
        assignReturnId.current = row.id;
        assignReturnExpectedDoctor.current = null;
        setAssignRow(row);
      }
      return;
    }
    const futurePlanning = dayScope === 'future' && requestedAction.action === 'cancel' && ['booked','confirmed'].includes(row.status);
    if (role !== 'receptionist' || ((dayScope ?? 'today') !== 'today' && !futurePlanning) ||
        busyRowId !== null || !dayBoardActions(row.status).includes(requestedAction.action)) return;
    runAction(row, requestedAction.action);
  }, [requestedAction, contextDisabled, rows, role, dayScope, busyRowId, onRequestedActionHandled]);

  return (
    <>
      <ClinicDayBoardView
        compactHome={compactHome}
        rows={rows}
        role={role}
        loading={loading}
        error={error}
        feedback={feedback}
        busyRowId={busyRowId}
        highlightAppointmentId={highlightAppointmentId}
        scrollHighlightIntoView={scrollHighlightIntoView}
        onAction={runAction}
        contextDisabled={contextDisabled}
        onOpenRow={(row) => {
          if (contextDisabled) return;
          if (role === 'receptionist' && receptionRowOpenTarget(row, dayScope ?? 'today', intakeSpineEnabled) === 'registration') {
            openRowIntake(row);
            return;
          }
          // Reception's appointment quick view already supports registration
          // recorded on an unlinked lead. Its saved-patient detail actions stay
          // gated by hasPatientRecord; an appointment is never an assigned task.
          if (role === 'receptionist' && onOpenPatientRecord) {
            onOpenPatientRecord(row);
            return;
          }
          // Role-shaped drawers (DOCTOR-CONTEXT LAW): the doctor gets the
          // doctor-safe context drawer; leads-workspace roles keep the ONE
          // LeadQuickView; everyone else has no door (the view renders none).
          const kind = rowOpenKind(role, row);
          if (kind === 'doctor_context') {
            setOpenDoctorRow({
              appointmentId: row.id,
              title: dayBoardPatientLabel(row),
            });
          } else if (kind === 'lead_quickview' && row.leadId != null) {
            if (onOpenLead) { onOpenLead(row.leadId, dayBoardPatientLabel(row)); return; }
            setOpenLead({
              leadId: row.leadId,
              // V-2: golden-record-first seed (linked full_name wins over the
              // booking name), consistent with the row label; the quick view
              // then loads its own authoritative model.
              title: dayBoardPatientLabel(row),
            });
          }
        }}
        {...(onOpenPatientRecord ? { onOpenPatientRecord } : {})}
        {...(paymentAction ? { paymentAction } : {})}
        {...(caseSheetAction ? { caseSheetAction } : {})}
        {...(contactRevealAction ? { contactRevealAction } : {})}
        {...(chrome ? { chrome } : {})}
        {...(patientRecordDoor ? { patientRecordDoor } : {})}
        {...(clinicDoctors ? { clinicDoctors } : {})}
        {...(typeof nowMs === 'number' ? { nowMs } : {})}
        {...(statusLabels ? { statusLabels } : {})}
        {...(dayScope ? { dayScope } : {})}
        intakeSpineEnabled={intakeSpineEnabled}
        onIntake={openRowIntake}
        onAssignDoctor={(row) => {
          setFeedback(null);
          assignReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          assignReturnId.current = row.id;
          assignReturnExpectedDoctor.current = null;
          setAssignRow(row);
        }}
      />
      {openLead ? (
        <LeadQuickViewHost
          leadId={openLead.leadId}
          seedTitle={openLead.title}
          onClose={() => setOpenLead(null)}
          onChanged={onChanged}
        />
      ) : null}
      {openDoctorRow ? (
        <DoctorQueueContextDrawer
          appointmentId={openDoctorRow.appointmentId}
          seedTitle={openDoctorRow.title}
          onClose={() => setOpenDoctorRow(null)}
        />
      ) : null}
      {identityStep ? (
        <CheckinIdentityStep
          row={identityStep.row}
          action={identityStep.action}
          bridgeEnabled={checkinIdentity?.bridgeEnabled === true}
          onProceed={(identityChecked = false) => {
            const held = identityStep;
            setIdentityStep(null);
            fireAction(held.row, held.action, undefined, identityChecked);
          }}
          onClose={() => setIdentityStep(null)}
          onLinked={onChanged}
        />
      ) : null}
      {visitConfirmation ? (
        <ModalShell
          title={visitConfirmation.action === 'arrived' ? 'Mark arrived' : 'Check in'}
          workspace hideCloseButton trapFocus
          closeOnEscape={busyRowId !== visitConfirmation.row.id}
          closeOnScrim={busyRowId !== visitConfirmation.row.id}
          testId="reception-visit-confirmation"
          onClose={() => setVisitConfirmation(null)}

        >

          <p>{dayBoardPatientLabel(visitConfirmation.row)}{visitConfirmation.row.linkedPatientUmr ? ` · ${visitConfirmation.row.linkedPatientUmr}` : ''}</p>
          <p>Appointment #{visitConfirmation.row.id} · {formatIstDateTime(visitConfirmation.row.appointmentTime)}</p>
          <p>{visitConfirmation.row.assignedDoctorName || DOCTOR_UNASSIGNED_LABEL}</p>
          {visitConfirmation.returning ? <p data-testid="reception-visit-returning">Returning patient · registration on file</p> : null}
{visitConfirmation.action === 'checked_in' && !visitConfirmation.returning ? <label><Checkbox checked={visitIdentityChecked} onCheckedChange={checked=>setVisitIdentityChecked(checked)}/> Identity checked</label> : null}
<div className="p1-arrival-actions">

            <Button className="ac-btn ac-btn--primary" data-testid="reception-visit-confirm" disabled={busyRowId === visitConfirmation.row.id || loading || error || (visitConfirmation.action === 'checked_in' && !visitConfirmation.returning && !visitIdentityChecked)}
              onClick={() => {
            if (visitSaving.current) return;
            const { row: held, action, returning } = visitConfirmation;
            const current = rows.find(row => row.id === held.id);
            if (!current || current.status !== held.status || current.patientId !== held.patientId ||
                current.clinicId !== held.clinicId || current.appointmentTime !== held.appointmentTime ||
                current.assignedDoctorId !== held.assignedDoctorId ||
                (returning === true && !isReturningCheckInRow(current)) ||
                role !== 'receptionist' || (dayScope ?? 'today') !== 'today') {
              setVisitConfirmation(null);
              setFeedback({ tone: 'error', text: 'This appointment changed. Review the day before acting.' });
              onChanged();
              return;
            }
            if (action !== 'arrived' && dayBoardActionRoute(current, action, { intakeSpineEnabled }) === 'identity_step') {
              setVisitConfirmation(null);
              setIdentityStep({ row: current, action });
            } else if (returning === true) fireAction(current, action, undefined, true, true);
            else fireAction(current, action, undefined, visitIdentityChecked);
          }}>{busyRowId === visitConfirmation.row.id ? 'Saving...' : visitConfirmation.action === 'arrived' ? 'Mark arrived' : 'Check in'}</Button>
            <Button variant="ghost" className="ac-btn ac-btn--ghost" data-testid="reception-visit-cancel" disabled={busyRowId === visitConfirmation.row.id}
              onClick={() => setVisitConfirmation(null)}>Keep unchanged</Button></div>
          {feedback?.tone === 'error' ? <p role="alert">{feedback.text}</p> : null}
        </ModalShell>
      ) : null}
      {cancelRow ? (
        <CancelReasonDialog
          key={cancelRow.id}
          details={<>
            <FieldGrid columns={1} fields={[
              {label: 'Appointment', value: `#${cancelRow.id}`},
              {label: 'Date / time', value: appointmentDateTime(cancelRow.appointmentTime)},
              {label: 'Clinic / doctor', value: `${(bookingClinic?.id === (cancelRow.clinicId ?? bookingClinic?.id) ? bookingClinic?.label : null) || 'Not recorded'} · ${cancelRow.assignedDoctorName || 'Not recorded'}`},
              {label: 'State', value: appointmentStatusDisplayLabel(cancelRow.status)},
            ]}/>
            {(cancelRow.clinicId ?? bookingClinic?.id) != null
              ? <SavedBookingDetails appointmentId={cancelRow.id} patientId={null} clinicId={(cancelRow.clinicId ?? bookingClinic?.id)!}/>
              : <p>Saved booking details unavailable.</p>}
          </>}
          patientName={dayBoardPatientLabel(cancelRow)}
          appointmentLabel={`${appointmentDateTime(cancelRow.appointmentTime)} · ${cancelRow.assignedDoctorName || 'Not recorded'}`}
          codes={cancelReasonCodes}
          busy={busyRowId === cancelRow.id}
          error={feedback?.tone === 'error' ? feedback.text : null}
          onConfirm={(reasonCode) => fireAction(cancelRow, 'cancel', reasonCode)}
          onClose={() => {
            const id = cancelRow.id;
            setCancelRow(null);
            requestAnimationFrame(() => (document.querySelector<HTMLElement>('[data-testid="home-quick-action-cancel"]') ?? document.querySelector<HTMLElement>(`[data-testid="day-board-open-${id}"]`))?.focus());
          }}
        />
      ) : null}
      {noShowRow ? (
        <ConfirmDialog
          eyebrow="Reception · appointment status"
          title="Mark this patient no-show?"
          confirmLabel="Mark no-show"
          cancelLabel="Keep appointment"
          destructive
          busy={busyRowId === noShowRow.id}
          busyLabel="Saving..."
          onCancel={() => setNoShowRow(null)}
          onConfirm={() => fireAction(noShowRow, 'no_show')}
          testId="reception-no-show-dialog"
        >
          <p>{dayBoardPatientLabel(noShowRow)}</p>
          <p>Appointment #{noShowRow.id} · {formatIstDateTime(noShowRow.appointmentTime)}</p>
          <p>{NO_SHOW_CONFIRM_COPY}</p>
        </ConfirmDialog>
      ) : null}
      {fulfillRow ? (
        <ConfirmDialog
          eyebrow="Reception · visit status"
          title="Mark this visit fulfilled?"
          confirmLabel="Mark fulfilled"
          cancelLabel={fulfillRow.status === 'arrived' ? 'Keep arrived' : 'Keep checked in'}
          destructive
          busy={busyRowId === fulfillRow.id}
          busyLabel="Saving..."
          onCancel={() => setFulfillRow(null)}
          onConfirm={() => {
            const current = rows.find((row) => row.id === fulfillRow.id);
            if (
              role !== 'receptionist' ||
              (dayScope ?? 'today') !== 'today' ||
              contextDisabled ||
              current === undefined ||
              !sameFulfillmentTarget(fulfillRow, current)
            ) {
              setFulfillRow(null);
              setFeedback({ tone: 'error', text: 'This appointment changed. Review the day before acting.' });
              onChanged();
              return;
            }
            fireAction(current, 'fulfilled');
          }}
          testId="reception-fulfill-dialog"
          confirmTestId="reception-fulfill-confirm"
          cancelTestId="reception-fulfill-keep"
        >
          <p data-testid="reception-fulfill-patient">{dayBoardPatientLabel(fulfillRow)}</p>
          {fulfillRow.linkedPatientUmr ? <p>UMR {fulfillRow.linkedPatientUmr}</p> : null}
          <p>{formatIstDateTime(fulfillRow.appointmentTime) || 'Appointment time unavailable'} · {fulfillRow.assignedDoctorName || DOCTOR_UNASSIGNED_LABEL}</p>
          <p>Current state: {fulfillRow.status === 'arrived' ? 'Arrived' : 'Checked in'}. Confirm only after the visit is complete.</p>
          {feedback?.tone === 'error' ? <p role="alert">{feedback.text}</p> : null}
        </ConfirmDialog>
      ) : null}
      {assignRow ? (
        <AssignDoctorDialog
          doctors={clinicDoctors ?? []}
          currentDoctorId={assignRow.assignedDoctorId ?? null}
          currentDoctorName={assignRow.assignedDoctorName}
          patientName={dayBoardPatientLabel(assignRow)}
          appointmentLabel={`Appointment #${assignRow.id} · ${formatIstDateTime(assignRow.appointmentTime)}`}
          busy={busyRowId === assignRow.id}
          terminal={assignmentStopped.has(assignRow.id)}
          onConfirm={(doctorId, reasonCode) => fireAssignDoctor(assignRow, doctorId, reasonCode)}
          onClose={() => setAssignRow(null)}
          error={feedback?.tone === 'error' ? feedback.text : null}
        />
      ) : null}
      {intakeRow ? (
        <IntakeDrawer
          appointmentId={intakeRow.id}
          appointmentStatus={intakeRow.status}
          patientLabel={dayBoardPatientLabel(intakeRow)}
          intakeStatus={intakeRow.intakeStatus}
          intakeMode={intakeRow.intakeMode}
          onClose={() => setIntakeRow(null)}
          onChanged={onChanged}
        />
      ) : null}
      {registrationRow ? (
        <CompleteRegistrationModal
          key={registrationRow.id}
          appointmentId={registrationRow.id}
          patientLabel={dayBoardPatientLabel(registrationRow)}
          startInForm={registrationStartInForm}
          onClose={() => {
            setRegistrationRow(null);
            setRegistrationStartInForm(false);
          }}
          onChanged={onChanged}
        />
      ) : null}
    </>
  );
}
